import { useState, useEffect, useRef, useMemo, ReactNode, Component } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { useAdaptiveExperienceOptional } from '../context/AdaptiveExperienceContext';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';
import { NotificationStatusBanner } from '../components/NotificationStatusBanner';
import { EnhancedShareButton } from '../components/EnhancedShareButton';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MicroActions } from '../components/MicroActions';
import { RestraintTimer } from '../components/RestraintTimer';
import { CheckInStreaks } from '../components/CheckInStreaks';
import { MonthlyRetrospective } from '../components/MonthlyRetrospective';
import { WeeklyReportCard } from '../components/WeeklyReportCard';
import { CommunicationTemplates } from '../components/CommunicationTemplates';
import { RuminationDetection } from '../components/RuminationDetection';
import { ContextualPrompts } from '../components/ContextualPrompts';
import {
  ArrowRight,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  Crown,
  Lock,
  Package,
  Heart,
  ChevronDown,
  Sparkles,
  Zap,
  ShieldCheck,
  ShieldAlert,
  CalendarClock,
  BookOpen,
  LineChart,
  Users,
  Search,
  Send,
  MessageCircle,
  UserPlus,
  Compass,
} from 'lucide-react';
import { InfoHint } from '../components/InfoHint';
import { refreshMicroActions } from '../lib/micro-actions';
import { CHECKINS_UPDATED_EVENT } from '../lib/cloud-sync';
import { useCheckInNotifications, usePartnerNotifications } from '../hooks/useNotifications';
import { toast } from 'sonner';
import { computeChurnRisk, computeForecastV2, detectPatternLibrary, generateConversationScripts } from '../lib/forecast-v2';
import { useRelationshipIntelligenceOptional } from '../context/RelationshipIntelligenceContext';
import type { CounterfactualStyle } from '../lib/relationship-intelligence-engine';
import {
  CONSUMER_LOGIN_SNAPSHOT_UPDATED_EVENT,
  readLatestConsumerLoginSnapshot,
  type ConsumerLoginSnapshot,
} from '../lib/consumer-login-snapshot';
import { safeLocalStorageGet, safeReadJsonFromStorage } from '../lib/storage-utils';
import { copyToClipboard } from '../utils/clipboard';
import {
  requestTensionResponse,
  requestTensionResponseStream,
  updateTensionLiveSession,
  type TensionContextType,
  type TensionEmotion,
  type TensionRespondPayload,
  type TensionStreamPartialEvent,
} from '../lib/tension-api';
import { createRequestVersionGuard } from '../lib/request-version-guard';
import { canAccessSocialNetwork } from '../lib/social-rollout';
import { analytics } from '../lib/analytics';
import { projectId } from '/utils/supabase/info';
import {
  commentOnSocialPost,
  createSocialPost,
  getSocialFeed,
  getSocialIntelligenceRecommendations,
  getSocialOverview,
  getSocialSuggestions,
  searchSocialProfiles,
  toggleSocialFollow,
  reactToSocialPost,
  getSocialMutuals,
} from '../lib/social-api';
import { PERFORMANCE_MODE_KEY, readPerformanceMode, type PerformanceMode } from '../lib/performance-mode';
import { readSocialTuningSettings, type SocialTuningSettings } from '../lib/social-tuning';
import { recordHomeSocialConversionEvent } from '../lib/home-social-conversion';
import {
  HOME_COMMUNITY_ASSIST_EXPERIMENT_KEY,
  HOME_COMMUNITY_ASSIST_VARIANT_KEY,
  readHomeCommunityAssistExperimentConfig,
  type HomeCommunityAssistVariant,
} from '../lib/home-community-assist-experiment';
import {
  DEFAULT_HOME_RI_EXPERIMENT_CONFIG,
  getHomeRiRolloutRecommendation,
  getHomeRiRolloutCooldownRemainingMs,
  getHomeRiExperimentHealth,
  HOME_RI_EXPERIMENT_CONFIG_KEY,
  HOME_RI_VARIANT_KEY,
  isHomeRiRolloutChangeLocked,
  isHomeRiRolloutEligible,
  markHomeRiRolloutChange,
  maybeAutoPromoteHomeRiVariant,
  readHomeRiExperimentConfig,
  recordHomeRiExperimentMetric,
  resetHomeRiExperimentStats,
  resolveHomeRiVariant,
  writeHomeRiExperimentConfig,
  type HomeRiVariant,
} from '../lib/home-ri-experiment';
import { recordUiInfraRun } from '../lib/ui-infra-baseline';
import {
  type RealtimeChatTurn,
} from '../components/tension/RealtimeConversationStream';
import { TrendTimingRailCard } from '../components/relationship-intelligence/TrendTimingRailCard';
import { MessagePathComposerCard } from '../components/relationship-intelligence/MessagePathComposerCard';
import { RepairMissionTimelineCard } from '../components/relationship-intelligence/RepairMissionTimelineCard';
import { StabilityCockpitCard } from '../components/relationship-intelligence/StabilityCockpitCard';
import { IntelligenceBriefing } from '../components/relationship-intelligence/IntelligenceBriefing';

function HomeRtiRing({ score, state }: { score: number; state: string }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, score));
  const offset = c * (1 - pct / 100);
  const stroke =
    state === 'Stable' ? '#22c55e' : state === 'Elastic' ? '#eab308' : '#ef4444';
  return (
    <div className="relative mx-auto h-44 w-44">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden>
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          className="stroke-neutral-200 dark:stroke-neutral-700"
          strokeWidth="10"
        />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{score}</span>
        <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-neutral-500">RTI</span>
      </div>
    </div>
  );
}

class NamedHomeErrorBoundary extends Component<
  { name: string; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { name: string; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error(`🚨 Home render error in "${this.props.name}"`, error);
    if (errorInfo?.componentStack) {
      console.error(`Home "${this.props.name}" component stack:`, errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

type HomeCoexistenceDay = {
  coreActions: number;
  socialActions: number;
  socialPanelExpands: number;
  socialActionsAfterCore: number;
  gateBlocks: number;
};

type HomeCoexistenceTelemetry = {
  byDay: Record<string, HomeCoexistenceDay>;
  updatedAt: string;
};

const HOME_COEXISTENCE_STORAGE_KEY = 'tension_home_coexistence_v1';
const HOME_COEXISTENCE_MAX_DAYS = 21;
const HOME_SOCIAL_QUALITY_MODE_KEY = 'tension_home_social_quality_mode';
const HOME_SOCIAL_MISSION_STATE_KEY = 'tension_home_social_mission_state_v1';
const HOME_SOCIAL_MOMENTUM_STATE_KEY = 'tension_home_social_momentum_state_v1';
const HOME_RI_MISSION_STATE_KEY = 'tension_home_ri_mission_state_v1';
type SocialQualityMode = 'balanced' | 'strict';
type HomeSectionKey = 'coreFlow' | 'community' | 'insights' | 'tools';
type HomeSectionState = Record<HomeSectionKey, boolean>;
type TensionEmotionState = {
  userEmotion: 'confused' | 'hurt' | 'calm' | 'defensive';
  intensity: number;
  relationshipRisk: 'low' | 'medium' | 'high';
  clarityNeed: 'low' | 'high';
};
type RiMicroScenarioId = 'steady' | 'curious' | 'repair';

const HOME_FLOW_SECTIONS_STORAGE_KEY = 'tension_home_flow_sections_v1';
const DEFAULT_HOME_SECTION_STATE: HomeSectionState = {
  coreFlow: true,
  community: false,
  insights: true,
  tools: false,
};
const RI_MICRO_SCENARIOS: Array<{
  id: RiMicroScenarioId;
  label: string;
  cue: string;
  action: string;
}> = [
  {
    id: 'steady',
    label: 'Keep calm',
    cue: 'Signals look mostly stable',
    action: 'Mirror the best recent pattern and avoid over-correcting.',
  },
  {
    id: 'curious',
    label: 'Lead with curiosity',
    cue: 'Mixed signals and unclear intent',
    action: 'Use one short curiosity opener and stop after one follow-up.',
  },
  {
    id: 'repair',
    label: 'Start repair',
    cue: 'Tension is rising after a miss',
    action: 'Acknowledge impact, own one miss, and offer one specific repair step.',
  },
];

const TENSION_HOME_CONTEXT_OPTIONS: Array<{ id: TensionContextType; label: string }> = [
  { id: 'misunderstanding', label: 'misunderstanding' },
  { id: 'tone_issue', label: 'tone issue' },
  { id: 'emotional_distance', label: 'emotional distance' },
  { id: 'argument_starting', label: 'argument starting' },
  { id: 'other', label: 'other' },
];

const TENSION_HOME_EMOTION_OPTIONS: Array<{ id: TensionEmotion; label: string }> = [
  { id: 'confused', label: 'confused' },
  { id: 'overwhelmed', label: 'overwhelmed' },
  { id: 'hurt', label: 'hurt' },
  { id: 'frustrated', label: 'frustrated' },
  { id: 'anxious', label: 'anxious' },
];

function readHomeSectionState(): HomeSectionState {
  try {
    const raw = safeLocalStorageGet(HOME_FLOW_SECTIONS_STORAGE_KEY);
    if (!raw) return DEFAULT_HOME_SECTION_STATE;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_HOME_SECTION_STATE;
    return {
      coreFlow: typeof parsed.coreFlow === 'boolean' ? parsed.coreFlow : DEFAULT_HOME_SECTION_STATE.coreFlow,
      community: typeof parsed.community === 'boolean' ? parsed.community : DEFAULT_HOME_SECTION_STATE.community,
      insights: typeof parsed.insights === 'boolean' ? parsed.insights : DEFAULT_HOME_SECTION_STATE.insights,
      tools: typeof parsed.tools === 'boolean' ? parsed.tools : DEFAULT_HOME_SECTION_STATE.tools,
    };
  } catch {
    return DEFAULT_HOME_SECTION_STATE;
  }
}

function HomeFlowSection({
  sectionId,
  title,
  description,
  isOpen,
  onToggle,
  children,
}: {
  sectionId?: string;
  title: string;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section
      id={sectionId}
      className="rounded-2xl border border-neutral-200/80 bg-white/75 p-4 dark:border-neutral-700/80 dark:bg-neutral-900/50"
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex min-h-11 w-full items-start justify-between gap-3 rounded-xl text-left touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        aria-expanded={isOpen}
      >
        <div>
          <h2 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">{title}</h2>
          <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">{description}</p>
        </div>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {isOpen ? <div className="mt-4 space-y-4">{children}</div> : null}
    </section>
  );
}

function todayIsoKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateKeyDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function defaultHomeCoexistenceDay(): HomeCoexistenceDay {
  return {
    coreActions: 0,
    socialActions: 0,
    socialPanelExpands: 0,
    socialActionsAfterCore: 0,
    gateBlocks: 0,
  };
}

function readHomeCoexistenceTelemetry(): HomeCoexistenceTelemetry {
  try {
    const raw = localStorage.getItem(HOME_COEXISTENCE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') {
      return { byDay: {}, updatedAt: new Date().toISOString() };
    }
    return {
      byDay: parsed.byDay && typeof parsed.byDay === 'object' ? parsed.byDay : {},
      updatedAt:
        typeof parsed.updatedAt === 'string' && parsed.updatedAt
          ? parsed.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return { byDay: {}, updatedAt: new Date().toISOString() };
  }
}

function writeHomeCoexistenceTelemetry(payload: HomeCoexistenceTelemetry): void {
  try {
    localStorage.setItem(HOME_COEXISTENCE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // noop
  }
}

function recordHomeCoexistenceEvent(
  kind: 'core' | 'social' | 'expand' | 'social_after_core' | 'gate_block',
): void {
  const telemetry = readHomeCoexistenceTelemetry();
  const key = todayIsoKey();
  const current = telemetry.byDay[key] ?? defaultHomeCoexistenceDay();
  const next: HomeCoexistenceDay = { ...current };
  if (kind === 'core') next.coreActions += 1;
  if (kind === 'social') next.socialActions += 1;
  if (kind === 'expand') next.socialPanelExpands += 1;
  if (kind === 'social_after_core') next.socialActionsAfterCore += 1;
  if (kind === 'gate_block') next.gateBlocks += 1;
  telemetry.byDay[key] = next;

  const keys = Object.keys(telemetry.byDay).sort();
  if (keys.length > HOME_COEXISTENCE_MAX_DAYS) {
    const toDrop = keys.slice(0, keys.length - HOME_COEXISTENCE_MAX_DAYS);
    toDrop.forEach((dropKey) => delete telemetry.byDay[dropKey]);
  }

  telemetry.updatedAt = new Date().toISOString();
  writeHomeCoexistenceTelemetry(telemetry);
}

type HomeMissionState = {
  dateKey: string;
  started: boolean;
  completed: boolean;
  startCircle?: string;
};

type HomeSocialMomentumState = {
  dateKey: string;
  follows: number;
  comments: number;
  posts: number;
  nudgesUsed: number;
};

type HomeRiMissionState = {
  dateKey: string;
  completedStepIds: string[];
  updatedAt: number;
};

function readHomeMissionState(): HomeMissionState | null {
  try {
    const raw = localStorage.getItem(HOME_SOCIAL_MISSION_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      dateKey: typeof parsed.dateKey === 'string' ? parsed.dateKey : todayIsoKey(),
      started: Boolean(parsed.started),
      completed: Boolean(parsed.completed),
      startCircle: typeof parsed.startCircle === 'string' ? parsed.startCircle : undefined,
    };
  } catch {
    return null;
  }
}

function readHomeSocialMomentumState(): HomeSocialMomentumState {
  const todayKey = todayIsoKey();
  try {
    const raw = localStorage.getItem(HOME_SOCIAL_MOMENTUM_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || parsed.dateKey !== todayKey) {
      return {
        dateKey: todayKey,
        follows: 0,
        comments: 0,
        posts: 0,
        nudgesUsed: 0,
      };
    }
    return {
      dateKey: todayKey,
      follows: Math.max(0, Number(parsed.follows || 0)),
      comments: Math.max(0, Number(parsed.comments || 0)),
      posts: Math.max(0, Number(parsed.posts || 0)),
      nudgesUsed: Math.max(0, Number(parsed.nudgesUsed || 0)),
    };
  } catch {
    return {
      dateKey: todayKey,
      follows: 0,
      comments: 0,
      posts: 0,
      nudgesUsed: 0,
    };
  }
}

function writeHomeSocialMomentumState(state: HomeSocialMomentumState): void {
  try {
    localStorage.setItem(HOME_SOCIAL_MOMENTUM_STATE_KEY, JSON.stringify(state));
  } catch {
    // noop
  }
}

function writeHomeMissionState(state: HomeMissionState): void {
  try {
    localStorage.setItem(HOME_SOCIAL_MISSION_STATE_KEY, JSON.stringify(state));
  } catch {
    // noop
  }
}

function readHomeRiMissionState(): HomeRiMissionState {
  const todayKey = todayIsoKey();
  try {
    const raw = localStorage.getItem(HOME_RI_MISSION_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || parsed.dateKey !== todayKey) {
      return { dateKey: todayKey, completedStepIds: [], updatedAt: Date.now() };
    }
    const completedStepIds = Array.isArray(parsed.completedStepIds)
      ? parsed.completedStepIds.map((id: unknown) => String(id))
      : [];
    return {
      dateKey: todayKey,
      completedStepIds,
      updatedAt: Number(parsed.updatedAt || Date.now()),
    };
  } catch {
    return { dateKey: todayKey, completedStepIds: [], updatedAt: Date.now() };
  }
}

function writeHomeRiMissionState(state: HomeRiMissionState): void {
  try {
    localStorage.setItem(HOME_RI_MISSION_STATE_KEY, JSON.stringify(state));
  } catch {
    // noop
  }
}

function normalizedBucket(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 100;
}

function readHomeCommunityAssistVariant(userId: string): HomeCommunityAssistVariant {
  const experimentConfig = readHomeCommunityAssistExperimentConfig();
  if (experimentConfig.forcedVariant) {
    return experimentConfig.forcedVariant;
  }
  try {
    const cached = localStorage.getItem(HOME_COMMUNITY_ASSIST_VARIANT_KEY);
    if (cached === 'minimal' || cached === 'richer') {
      return cached;
    }
  } catch {
    // noop
  }
  const source = userId || 'anonymous';
  const variant: HomeCommunityAssistVariant = normalizedBucket(source) % 2 === 0 ? 'minimal' : 'richer';
  try {
    localStorage.setItem(HOME_COMMUNITY_ASSIST_VARIANT_KEY, variant);
  } catch {
    // noop
  }
  return variant;
}

function Home() {
  // ALL HOOKS MUST BE AT THE TOP - BEFORE ANY CONDITIONALS
  const [rti, setRti] = useState<any>(null);
  const [action, setAction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState<any>(null);
  const [socialFeed, setSocialFeed] = useState<Array<any>>([]);
  const [socialOverview, setSocialOverview] = useState<any>(null);
  const [socialSuggestions, setSocialSuggestions] = useState<Array<any>>([]);
  const [socialSuggestionContext, setSocialSuggestionContext] = useState<{
    source: 'intelligence' | 'fallback';
    policyVersion: number | null;
  }>({ source: 'fallback', policyVersion: null });
  const [socialMutualsByUser, setSocialMutualsByUser] = useState<Record<string, any>>({});
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialPostDraft, setSocialPostDraft] = useState('');
  const [socialFriendQuery, setSocialFriendQuery] = useState('');
  const [socialFriendResults, setSocialFriendResults] = useState<Array<any>>([]);
  const [socialSearching, setSocialSearching] = useState(false);
  const [socialCommentDrafts, setSocialCommentDrafts] = useState<Record<string, string>>({});
  const [socialBusyPost, setSocialBusyPost] = useState(false);
  const [socialActionBusy, setSocialActionBusy] = useState(false);
  const [pendingFollowIds, setPendingFollowIds] = useState<Record<string, boolean>>({});
  const [pendingCommentIds, setPendingCommentIds] = useState<Record<string, boolean>>({});
  const [optimisticPostIds, setOptimisticPostIds] = useState<Record<string, boolean>>({});
  const [socialActionRecap, setSocialActionRecap] = useState<{
    message: string;
    nextLabel: string | null;
  } | null>(null);
  const [socialMomentumState, setSocialMomentumState] = useState<HomeSocialMomentumState>(
    readHomeSocialMomentumState(),
  );
  const [socialTuning, setSocialTuning] = useState<SocialTuningSettings>(() => readSocialTuningSettings());
  const [socialQualityMode, setSocialQualityMode] = useState<SocialQualityMode>(() =>
    safeLocalStorageGet(HOME_SOCIAL_QUALITY_MODE_KEY) === 'strict' ? 'strict' : 'balanced',
  );
  const [showSocialUnlockCelebration, setShowSocialUnlockCelebration] = useState(false);
  const [canUseEnhancedMotion, setCanUseEnhancedMotion] = useState(false);
  const [performanceMode, setPerformanceMode] = useState<PerformanceMode>(() => readPerformanceMode());
  const [communityAssistVariant, setCommunityAssistVariant] = useState<HomeCommunityAssistVariant>(() =>
    readHomeCommunityAssistVariant('anonymous'),
  );
  const [showHomeSocialPanel, setShowHomeSocialPanel] = useState<boolean>(() =>
    safeLocalStorageGet('tension_home_social_expanded') === '1',
  );
  const [homeSections, setHomeSections] = useState<HomeSectionState>(() => readHomeSectionState());
  const [tensionQuickContext, setTensionQuickContext] = useState<TensionContextType>('misunderstanding');
  const [tensionQuickEmotion, setTensionQuickEmotion] = useState<TensionEmotion>('confused');
  const [tensionQuickSituation, setTensionQuickSituation] = useState('');
  const [tensionQuickResponse, setTensionQuickResponse] = useState<TensionRespondPayload | null>(null);
  const [tensionQuickTurns, setTensionQuickTurns] = useState<RealtimeChatTurn[]>([]);
  const [tensionQuickBusy, setTensionQuickBusy] = useState(false);
  const [tensionQuickPhaseLabel, setTensionQuickPhaseLabel] = useState('idle');
  const [tensionQuickPartial, setTensionQuickPartial] = useState<TensionStreamPartialEvent | null>(null);
  const [tensionQuickLiveSignal, setTensionQuickLiveSignal] = useState<{
    classification: string;
    riskLevel: 'low' | 'medium' | 'high';
  } | null>(null);
  const [tensionQuickSessionId] = useState(() => crypto.randomUUID());
  const tensionRealtimeGuardRef = useRef(createRequestVersionGuard());
  const tensionRealtimeLastAutoSignatureRef = useRef('');
  const tensionRealtimeInFlightSignatureRef = useRef<string | null>(null);
  const tensionRealtimeDraftSignatureRef = useRef('');
  const tensionRealtimeRunTokenRef = useRef(0);
  const [showRelationshipMenu, setShowRelationshipMenu] = useState(false);
  const relationshipMenuRef = useRef<HTMLDivElement | null>(null);
  const fetchRequestGuardRef = useRef(createRequestVersionGuard());
  const { user, accessToken } = useAuth();
  const { plan: liveSubscriptionPlan, loading: subscriptionPlanLoading } = useSubscriptionPlan();
  const navigate = useNavigate();
  const location = useLocation();
  const adaptive = useAdaptiveExperienceOptional();
  const relationshipIntel = useRelationshipIntelligenceOptional();
  const [counterfactualStyle, setCounterfactualStyle] = useState<CounterfactualStyle>('soft_startup');
  const [riMissionState, setRiMissionState] = useState<HomeRiMissionState>(() => readHomeRiMissionState());
  const [riExperimentConfig, setRiExperimentConfig] = useState(() => readHomeRiExperimentConfig());
  const [riVariant, setRiVariant] = useState<HomeRiVariant>('control');
  const [riRolloutEligible, setRiRolloutEligible] = useState(false);
  const [riHealthRevision, setRiHealthRevision] = useState(0);
  const [riResetBusy, setRiResetBusy] = useState(false);
  const [riCooldownClockMs, setRiCooldownClockMs] = useState(() => Date.now());
  const [riMicroScenario, setRiMicroScenario] = useState<RiMicroScenarioId>('curious');
  const [riMicroEnergy, setRiMicroEnergy] = useState(62);
  const [riMicroRevealStep, setRiMicroRevealStep] = useState(1);
  const [loginSnapshot, setLoginSnapshot] = useState<ConsumerLoginSnapshot | null>(() =>
    readLatestConsumerLoginSnapshot(user?.id),
  );
  const isAdmin = Boolean(
    user?.user_metadata?.is_admin === 'true' ||
      user?.user_metadata?.role === 'admin' ||
      user?.email?.endsWith('@tensionlabs.net'),
  );
  const socialRollout = canAccessSocialNetwork(user?.id || '', isAdmin);
  const riSectionViewedRef = useRef(false);

  // LocalStorage values can be stale from older builds. React throws error #130
  // if we try to render a plain object as a child, so we coerce everything we
  // might render into safe primitives here.
  const safeString = (v: any): string => {
    if (typeof v === 'string') return v;
    if (v === null || v === undefined) return '';
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };

  const safeNumber = (v: any, fallback = 0): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return fallback;
  };

  const safeComponents = (v: any) => {
    const obj = v && typeof v === 'object' ? v : {};
    return {
      initiation: safeNumber((obj as any).initiation, 0),
      latency: safeNumber((obj as any).latency, 0),
      emotionalDensity: safeNumber((obj as any).emotionalDensity, 0),
      availability: safeNumber((obj as any).availability, 0),
      withdrawal: safeNumber((obj as any).withdrawal, 0),
    };
  };

  // Enable notification hooks
  useCheckInNotifications();
  usePartnerNotifications();

  // Get relationships and active relationship
  const relationshipsRaw = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_relationships', []);
  const relationships = (Array.isArray(relationshipsRaw) ? relationshipsRaw : []).map((r: any) => ({
    id: safeString(r?.id),
    name: safeString(r?.name),
    type: safeString(r?.type),
    isActive: typeof r?.isActive === 'boolean' ? r.isActive : undefined,
  }));

  const activeRelationshipId = safeString(safeLocalStorageGet('tension_active_relationship'));
  const activeRelationship = relationships.find((r: any) => r.id === activeRelationshipId);
  const [appMode, setAppMode] = useState<'relationship' | 'single'>(
    () => (safeLocalStorageGet('tension_mode') === 'single' ? 'single' : 'relationship'),
  );
  const isCouplesMode = appMode === 'relationship';

  // Use live profile-backed plan first; local storage is fallback for unauthenticated sessions.
  const subscription = liveSubscriptionPlan || safeLocalStorageGet('tension_subscription') || 'free';
  const isPremium = subscription !== 'free';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!accessToken || !socialRollout.allowed) return;
    void loadHomeSocialData();
  }, [accessToken, socialRollout.allowed, socialTuning]);

  useEffect(() => {
    const onCheckInsUpdated = () => {
      fetchData();
    };
    window.addEventListener(CHECKINS_UPDATED_EVENT, onCheckInsUpdated);
    return () => {
      window.removeEventListener(CHECKINS_UPDATED_EVENT, onCheckInsUpdated);
    };
  }, []);

  useEffect(() => {
    setLoginSnapshot(readLatestConsumerLoginSnapshot(user?.id));
  }, [user?.id]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key?.startsWith('tension_consumer_login_snapshot_v1:')) {
        setLoginSnapshot(readLatestConsumerLoginSnapshot(user?.id));
      }
    };
    const onSnapshotUpdated = () => {
      setLoginSnapshot(readLatestConsumerLoginSnapshot(user?.id));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CONSUMER_LOGIN_SNAPSHOT_UPDATED_EVENT, onSnapshotUpdated);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CONSUMER_LOGIN_SNAPSHOT_UPDATED_EVENT, onSnapshotUpdated);
    };
  }, [user?.id]);

  useEffect(() => {
    try {
      localStorage.setItem('tension_home_social_expanded', showHomeSocialPanel ? '1' : '0');
    } catch {
      // noop
    }
  }, [showHomeSocialPanel]);

  useEffect(() => {
    try {
      localStorage.setItem(HOME_FLOW_SECTIONS_STORAGE_KEY, JSON.stringify(homeSections));
    } catch {
      // noop
    }
  }, [homeSections]);

  useEffect(() => {
    try {
      localStorage.setItem(HOME_SOCIAL_QUALITY_MODE_KEY, socialQualityMode);
    } catch {
      // noop
    }
  }, [socialQualityMode]);

  useEffect(() => {
    writeHomeRiMissionState(riMissionState);
  }, [riMissionState]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncTuning = () => setSocialTuning(readSocialTuningSettings());
    const syncQualityMode = () =>
      setSocialQualityMode(
        safeLocalStorageGet(HOME_SOCIAL_QUALITY_MODE_KEY) === 'strict' ? 'strict' : 'balanced',
      );
    const onStorage = () => {
      syncTuning();
      syncQualityMode();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onStorage);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!safeLocalStorageGet(HOME_RI_EXPERIMENT_CONFIG_KEY)) {
      writeHomeRiExperimentConfig(DEFAULT_HOME_RI_EXPERIMENT_CONFIG);
    }
    const syncRiConfig = () => {
      setRiExperimentConfig(readHomeRiExperimentConfig());
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === HOME_RI_EXPERIMENT_CONFIG_KEY) syncRiConfig();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncRiConfig);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncRiConfig);
    };
  }, []);

  useEffect(() => {
    const sourceId = user?.id || 'anonymous';
    const promoted = maybeAutoPromoteHomeRiVariant(riExperimentConfig);
    if (promoted.promotedTo && promoted.config.forcedVariant === promoted.promotedTo) {
      setRiExperimentConfig(promoted.config);
      analytics.track('ri_variant_promoted', {
        variant: promoted.promotedTo,
        source: 'auto_promote',
      });
    }

    const effectiveConfig = promoted.config || riExperimentConfig;
    const rolloutEligible = isHomeRiRolloutEligible(sourceId, effectiveConfig);
    const variant = resolveHomeRiVariant(sourceId, effectiveConfig);
    setRiVariant(variant);
    setRiRolloutEligible(rolloutEligible);
    try {
      localStorage.setItem(HOME_RI_VARIANT_KEY, variant);
    } catch {
      // noop
    }
    const assignmentKey = `tension_ri_assignment_logged:${todayIsoKey()}:${sourceId}:${variant}:${effectiveConfig.updatedAt}`;
    if (safeLocalStorageGet(assignmentKey) !== '1') {
      try {
        localStorage.setItem(assignmentKey, '1');
      } catch {
        // noop
      }
      analytics.track('ri_variant_assigned', {
        variant,
        enabled: effectiveConfig.enabled,
        rolloutPct: effectiveConfig.rolloutPct,
      });
      if (!effectiveConfig.enabled || !rolloutEligible) {
        analytics.track('ri_rollout_blocked', {
          reason: effectiveConfig.enabled ? 'bucket_control' : 'disabled',
        });
      } else {
        analytics.track('ri_rollout_enabled', { variant });
        recordHomeRiExperimentMetric(variant, 'impressions');
        setRiHealthRevision((value) => value + 1);
      }
    }
  }, [riExperimentConfig, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncMode = () => setAppMode(safeLocalStorageGet('tension_mode') === 'single' ? 'single' : 'relationship');
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'tension_mode') syncMode();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncMode);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncMode);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const computeMotionMode = () => {
      const performanceMode = safeLocalStorageGet('tension_performance_mode');
      if (performanceMode === 'minimal') {
        setCanUseEnhancedMotion(false);
        return;
      }
      const reduced = media.matches;
      const mobile = window.innerWidth < 768;
      const saveData = Boolean((navigator as any)?.connection?.saveData);
      setCanUseEnhancedMotion(!reduced && !mobile && !saveData);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'tension_performance_mode') {
        computeMotionMode();
      }
    };
    computeMotionMode();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', computeMotionMode);
    } else if (typeof media.addListener === 'function') {
      media.addListener(computeMotionMode);
    }
    window.addEventListener('resize', computeMotionMode);
    window.addEventListener('storage', onStorage);
    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', computeMotionMode);
      } else if (typeof media.removeListener === 'function') {
        media.removeListener(computeMotionMode);
      }
      window.removeEventListener('resize', computeMotionMode);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncMode = () => setPerformanceMode(readPerformanceMode());
    const onStorage = (event: StorageEvent) => {
      if (event.key === PERFORMANCE_MODE_KEY) {
        syncMode();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncMode);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncMode);
    };
  }, []);

  useEffect(() => {
    setCommunityAssistVariant(readHomeCommunityAssistVariant(user?.id || 'anonymous'));
  }, [user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncVariant = () => {
      setCommunityAssistVariant(readHomeCommunityAssistVariant(user?.id || 'anonymous'));
    };
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === HOME_COMMUNITY_ASSIST_VARIANT_KEY ||
        event.key === HOME_COMMUNITY_ASSIST_EXPERIMENT_KEY
      ) {
        syncVariant();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncVariant);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncVariant);
    };
  }, [user?.id]);

  const gateTransitionRef = useRef<boolean | null>(null);

  const trackCoreAction = (action: string) => {
    recordHomeCoexistenceEvent('core');
    analytics.track('home_core_action_started', {
      action,
      surface: 'home',
    });
  };

  const trackSocialAction = (action: string, afterCore: boolean, meta?: Record<string, any>) => {
    recordHomeCoexistenceEvent('social');
    if (afterCore) {
      recordHomeCoexistenceEvent('social_after_core');
    }
    analytics.track('home_social_action_taken', {
      action,
      afterCore,
      surface: 'home',
      ...(meta || {}),
    });
  };

  const markMissionCompletedIfActive = (trigger: 'post' | 'follow' | 'comment') => {
    const mission = readHomeMissionState();
    const todayKey = todayIsoKey();
    if (!mission || mission.dateKey !== todayKey || !mission.started || mission.completed) return;
    const nextMission: HomeMissionState = {
      ...mission,
      completed: true,
    };
    writeHomeMissionState(nextMission);
    analytics.track('home_social_mission_completed', {
      trigger,
      circle: mission.startCircle || null,
      qualityMode: socialQualityMode,
    });
    recordHomeSocialConversionEvent({ kind: 'mission_completed' });
  };

  // Close relationship switcher when clicking outside or pressing Escape
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        showRelationshipMenu &&
        relationshipMenuRef.current &&
        !relationshipMenuRef.current.contains(event.target as Node)
      ) {
        setShowRelationshipMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowRelationshipMenu(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showRelationshipMenu]);

  const fetchData = async () => {
    const requestId = fetchRequestGuardRef.current.next();
    setLoading(true); // Explicitly set loading to true at start
    try {
      // TEMPORARY: Use local storage instead of server
        const allCheckins = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_checkins', []);
      
      // Filter check-ins for active relationship
        const checkins = activeRelationshipId
          ? allCheckins.filter((c: any) => safeString(c?.relationshipId) === activeRelationshipId)
        : allCheckins;
      
      if (checkins.length > 0) {
        // Calculate RTI from most recent check-in
          const latest = checkins[checkins.length - 1] || {};
          const score = safeNumber(latest?.rtiScore, 0);
        const state = score <= 35 ? 'Stable' : score <= 60 ? 'Elastic' : 'High Tension';
        
          const components = safeComponents(latest?.components);
          if (!fetchRequestGuardRef.current.isCurrent(requestId)) return;
          setRti({ score, state, components });
        
        const scoreHistory = checkins
          .map((c: any) => safeNumber(c?.rtiScore, NaN))
          .filter((n: number) => Number.isFinite(n));
        const withdrawalHistory = checkins
          .map((c: any) => safeNumber(c?.components?.withdrawal, NaN))
          .filter((n: number) => Number.isFinite(n));
        const forecastV2 = computeForecastV2(scoreHistory);
        const patterns = detectPatternLibrary(scoreHistory, withdrawalHistory);
        const churnRisk = computeChurnRisk(scoreHistory, forecastV2);
        const attachmentStyle = (() => {
          const profile = safeReadJsonFromStorage<Record<string, unknown> | null>('tension_user_profile', null);
          return typeof profile?.attachmentStyle === 'string' ? profile.attachmentStyle : undefined;
        })();
        const scripts = generateConversationScripts({
          rtiScore: score,
          attachmentStyle,
          topPattern: patterns[0],
        });
        const forecastData = {
          ...getForecastTrend(forecastV2),
          churnRisk,
          patterns,
          scripts,
        };
        if (!fetchRequestGuardRef.current.isCurrent(requestId)) return;
        setForecast(forecastData);
        
        // Generate action based on state
        if (state === 'Stable') {
          if (!fetchRequestGuardRef.current.isCurrent(requestId)) return;
          setAction({
            type: 'stability',
            title: 'Maintain Momentum',
            description: 'Your relationship is in a healthy state. Continue your current patterns.',
            action: 'Keep your communication rhythm consistent'
          });
        } else if (state === 'Elastic') {
          if (!fetchRequestGuardRef.current.isCurrent(requestId)) return;
          setAction({
            type: 'reconnection',
            title: 'Light Reconnection',
            description: 'Small adjustments can help. Focus on quality time.',
            action: 'Plan a meaningful conversation or activity together'
          });
        } else {
          if (!fetchRequestGuardRef.current.isCurrent(requestId)) return;
          setAction({
            type: 'restraint',
            title: 'Practice Restraint',
            description: 'High tension detected. Give space for natural reconnection.',
            action: 'Reduce initiation frequency by 30% for the next 48 hours'
          });
        }
        
        // Atlas micro-actions (replaces today's batch; no duplicate stacking)
        try {
          await refreshMicroActions(score, components, {
            accessToken,
            source: 'home',
            // Home should remain calm even when Atlas is temporarily unavailable.
            showFallbackToast: false,
          });
        } catch (microActionError) {
          console.warn('Failed to refresh micro-actions:', microActionError);
        }
      }
      
      // Original server fetch code (commented out due to deployment issue)
      /*
      const rtiResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/rti`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const rtiData = await rtiResponse.json();
      setRti(rtiData.rti);

      const actionResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/action`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const actionData = await actionResponse.json();
      setAction(actionData.action);
      */
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      if (fetchRequestGuardRef.current.isCurrent(requestId)) {
        setLoading(false);
      }
    }
  };

  const loadHomeSocialData = async () => {
    if (!accessToken || !socialRollout.allowed) return;
    const startedAt = performance.now();
    let runStatus: 'ok' | 'error' = 'ok';
    let runReason: string | null = null;
    setSocialLoading(true);
    try {
      const [feedRes, overviewRes, intelligenceRes, suggestionsRes] = await Promise.allSettled([
        getSocialFeed(accessToken, 'discover', Math.max(8, socialTuning.friendSearchLimit)),
        getSocialOverview(accessToken),
        getSocialIntelligenceRecommendations(accessToken, '', socialTuning.friendSearchLimit),
        getSocialSuggestions(accessToken, 5),
      ]);
      if (feedRes.status !== 'fulfilled' || overviewRes.status !== 'fulfilled') {
        runStatus = 'error';
        runReason = 'feed_or_overview_unavailable';
      }
      if (feedRes.status === 'fulfilled') {
        setSocialFeed(Array.isArray(feedRes.value?.feed) ? feedRes.value.feed : []);
      }
      if (overviewRes.status === 'fulfilled') {
        setSocialOverview(overviewRes.value?.overview || null);
      }
      if (intelligenceRes.status === 'fulfilled') {
        const recommendations = Array.isArray(intelligenceRes.value?.recommendations)
          ? intelligenceRes.value.recommendations
          : [];
        if (recommendations.length > 0) {
          setSocialSuggestions(
            recommendations.map((candidate: any) => {
              const qualityScore = Number(candidate?.explain?.qualityScore || 0);
              const commonInterests = Number(candidate?.explain?.commonInterests || 0);
              const mutualCount = Number(candidate?.explain?.mutualCount || 0);
              const connectionFit = Math.min(
                99,
                Math.max(30, Math.round(40 + commonInterests * 10 + mutualCount * 8 + qualityScore * 0.35)),
              );
              return {
                userId: String(candidate?.userId || ''),
                displayName: String(candidate?.displayName || 'Community member'),
                headline: String(candidate?.headline || ''),
                interests: Array.isArray(candidate?.interests) ? candidate.interests : [],
                qualityScore,
                commonInterests,
                mutualCount,
                suggestionScore: Number(candidate?.score || 0),
                connectionFit,
                explain: candidate?.explain || null,
              };
            }),
          );
          setSocialSuggestionContext({
            source: 'intelligence',
            policyVersion: Number(intelligenceRes.value?.policyVersion || 0) || null,
          });
          return;
        }
      }
      if (suggestionsRes.status === 'fulfilled') {
        const suggestions = Array.isArray(suggestionsRes.value?.suggestions) ? suggestionsRes.value.suggestions : [];
        const mutualSignalMap = await loadMutualSignals(suggestions);
        const rankedSuggestions = [...suggestions]
          .sort((a, b) => {
            const aMutual = Number(mutualSignalMap[String(a?.userId || '')]?.mutualCount || 0);
            const bMutual = Number(mutualSignalMap[String(b?.userId || '')]?.mutualCount || 0);
            const aFit = getConnectionFitScore(a, mutualSignalMap);
            const bFit = getConnectionFitScore(b, mutualSignalMap);
            const aScore =
              aMutual * socialTuning.searchWeightMutual +
              aFit * socialTuning.searchWeightFit +
              Number(a?.commonInterests || 0) * socialTuning.searchWeightInterests;
            const bScore =
              bMutual * socialTuning.searchWeightMutual +
              bFit * socialTuning.searchWeightFit +
              Number(b?.commonInterests || 0) * socialTuning.searchWeightInterests;
            return bScore - aScore;
          })
          .slice(0, socialTuning.friendSearchLimit);
        setSocialSuggestions(rankedSuggestions);
        setSocialSuggestionContext({ source: 'fallback', policyVersion: null });
      }
    } catch (error) {
      console.error('Failed to load home social feed:', error);
      runStatus = 'error';
      runReason = error instanceof Error ? error.message : 'load_home_social_failed';
    } finally {
      setSocialLoading(false);
      recordUiInfraRun({
        surface: 'home_social_load',
        durationMs: performance.now() - startedAt,
        status: runStatus,
        reason: runReason,
      });
    }
  };

  const runFriendSearch = async (
    query: string,
    options?: { suppressErrors?: boolean },
  ): Promise<Array<any>> => {
    if (!accessToken) return;
    const normalized = query.trim();
    if (!normalized) {
      setSocialFriendResults([]);
      return [];
    }
    setSocialSearching(true);
    try {
      const result = await searchSocialProfiles(accessToken, normalized);
      const profiles = Array.isArray(result?.profiles) ? result.profiles : [];
      const mutualSignalMap = await loadMutualSignals(profiles);
      const normalizedLower = normalized.toLowerCase();
      const rankedProfiles = [...profiles]
        .sort((a, b) => {
          const aId = String(a?.userId || '');
          const bId = String(b?.userId || '');
          const aMutual = Number(mutualSignalMap[aId]?.mutualCount || 0);
          const bMutual = Number(mutualSignalMap[bId]?.mutualCount || 0);
          const aText = `${String(a?.displayName || '')} ${String(a?.headline || '')} ${String(a?.interests || '')}`.toLowerCase();
          const bText = `${String(b?.displayName || '')} ${String(b?.headline || '')} ${String(b?.interests || '')}`.toLowerCase();
          const aQueryBoost = aText.includes(normalizedLower) ? 30 : 0;
          const bQueryBoost = bText.includes(normalizedLower) ? 30 : 0;
          const aScore =
            aQueryBoost +
            aMutual * socialTuning.searchWeightMutual +
            getConnectionFitScore(a, mutualSignalMap) * socialTuning.searchWeightFit +
            Number(a?.commonInterests || 0) * socialTuning.searchWeightInterests;
          const bScore =
            bQueryBoost +
            bMutual * socialTuning.searchWeightMutual +
            getConnectionFitScore(b, mutualSignalMap) * socialTuning.searchWeightFit +
            Number(b?.commonInterests || 0) * socialTuning.searchWeightInterests;
          return bScore - aScore;
        })
        .slice(0, socialTuning.friendSearchLimit);
      setSocialFriendResults(rankedProfiles);
      trackSocialAction('friend_search', hasCoreActionToday);
      analytics.track('home_social_friend_search_ranked', {
        queryLength: normalized.length,
        totalResults: profiles.length,
        returnedResults: rankedProfiles.length,
        topMutualCount: Number(
          rankedProfiles[0]
            ? mutualSignalMap[String(rankedProfiles[0]?.userId || '')]?.mutualCount || 0
            : 0,
        ),
        qualityMode: socialQualityMode,
      });
      recordHomeSocialConversionEvent({
        kind: 'ranked_search',
        resultCount: rankedProfiles.length,
      });
      return rankedProfiles;
    } catch (error: any) {
      if (!options?.suppressErrors) {
        toast.error(error?.message || 'Search failed');
      }
      return [];
    } finally {
      setSocialSearching(false);
    }
  };

  const applySupportCircle = async (circle: string) => {
    setSocialFriendQuery(circle);
    await runFriendSearch(circle, { suppressErrors: true });
  };

  const handleHomeSocialPost = async () => {
    if (!accessToken) {
      toast.error('Sign in required');
      return;
    }
    const content = socialPostDraft.trim();
    if (!content) {
      toast.error('Write something first');
      return;
    }
    if (socialTuning.feedHideLowSignal && content.length < socialTuning.feedMinPostChars) {
      toast.error(`Add more context (${socialTuning.feedMinPostChars}+ chars) for a higher-quality post.`);
      return;
    }
    setSocialBusyPost(true);
    const optimisticId = `optimistic:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    const optimisticAuthor =
      String(user?.user_metadata?.name || '').trim() ||
      (user?.email ? String(user.email).split('@')[0] : 'You');
    const optimisticPost = {
      id: optimisticId,
      authorName: optimisticAuthor,
      createdAt: new Date().toISOString(),
      content,
      reactions: { like: 0, insight: 0, support: 0 },
    };
    setSocialFeed((prev) => [optimisticPost, ...prev]);
    setOptimisticPostIds((prev) => ({ ...prev, [optimisticId]: true }));
    setSocialPostDraft('');
    try {
      await createSocialPost(accessToken, { content, tags: ['home', 'community'] });
      trackSocialAction('post_create', hasCoreActionToday);
      setSocialMomentumState((prev) => {
        const todayKey = todayIsoKey();
        const base =
          prev.dateKey === todayKey
            ? prev
            : { dateKey: todayKey, follows: 0, comments: 0, posts: 0, nudgesUsed: 0 };
        const next = { ...base, posts: base.posts + 1 };
        writeHomeSocialMomentumState(next);
        return next;
      });
      markMissionCompletedIfActive('post');
      toast.success('Posted to social feed');
      await loadHomeSocialData();
    } catch (error: any) {
      setSocialFeed((prev) => prev.filter((post: any) => String(post?.id || '') !== optimisticId));
      setSocialPostDraft(content);
      toast.error(error?.message || 'Could not publish post');
    } finally {
      setOptimisticPostIds((prev) => {
        const next = { ...prev };
        delete next[optimisticId];
        return next;
      });
      setSocialBusyPost(false);
    }
  };

  const handleHomeSocialReact = async (postId: string, reactionType: 'like' | 'insight' | 'support') => {
    if (!accessToken) return;
    try {
      await reactToSocialPost(accessToken, postId, reactionType);
      trackSocialAction(`react_${reactionType}`, hasCoreActionToday);
      await loadHomeSocialData();
    } catch (error: any) {
      toast.error(error?.message || 'Reaction failed');
    }
  };

  const handleHomeSocialComment = async (postId: string) => {
    if (!accessToken) return;
    const content = (socialCommentDrafts[postId] || '').trim();
    if (!content) return;
    setPendingCommentIds((prev) => ({ ...prev, [postId]: true }));
    setSocialCommentDrafts((prev) => ({ ...prev, [postId]: '' }));
    try {
      await commentOnSocialPost(accessToken, postId, content);
      trackSocialAction('comment_create', hasCoreActionToday);
      setSocialMomentumState((prev) => {
        const todayKey = todayIsoKey();
        const base =
          prev.dateKey === todayKey
            ? prev
            : { dateKey: todayKey, follows: 0, comments: 0, posts: 0, nudgesUsed: 0 };
        const next = { ...base, comments: base.comments + 1 };
        writeHomeSocialMomentumState(next);
        return next;
      });
      markMissionCompletedIfActive('comment');
      await loadHomeSocialData();
    } catch (error: any) {
      setSocialCommentDrafts((prev) => ({ ...prev, [postId]: content }));
      toast.error(error?.message || 'Comment failed');
    } finally {
      setPendingCommentIds((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  };

  const handleFriendSearch = async () => {
    await runFriendSearch(socialFriendQuery);
  };

  const handleFriendFollow = async (
    targetUserId: string,
    source: 'search' | 'suggestion',
    rank: number,
  ) => {
    if (!accessToken) return;
    setPendingFollowIds((prev) => ({ ...prev, [targetUserId]: true }));
    try {
      const result = await toggleSocialFollow(accessToken, targetUserId);
      trackSocialAction('follow_toggle', hasCoreActionToday);
      if (result?.following) {
        analytics.track('home_social_follow_conversion', {
          source,
          rank,
          qualityMode: socialQualityMode,
          afterCore: hasCoreActionToday,
        });
        recordHomeSocialConversionEvent({
          kind: source === 'search' ? 'follow_search' : 'follow_suggestion',
        });
        setSocialMomentumState((prev) => {
          const todayKey = todayIsoKey();
          const base =
            prev.dateKey === todayKey
              ? prev
              : { dateKey: todayKey, follows: 0, comments: 0, posts: 0, nudgesUsed: 0 };
          const next = { ...base, follows: base.follows + 1 };
          writeHomeSocialMomentumState(next);
          return next;
        });
        markMissionCompletedIfActive('follow');
      }
      toast.success(result?.following ? 'Followed' : 'Unfollowed');
      await loadHomeSocialData();
      await handleFriendSearch();
    } catch (error: any) {
      toast.error(error?.message || 'Follow failed');
    } finally {
      setPendingFollowIds((prev) => {
        const next = { ...prev };
        delete next[targetUserId];
        return next;
      });
    }
  };

  const formatSocialTime = (value?: string) => {
    if (!value) return 'just now';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return 'just now';
    return dt.toLocaleString();
  };

  const loadMutualSignals = async (profiles: Array<any>) => {
    if (!accessToken || !Array.isArray(profiles) || profiles.length === 0) return {};
    const candidates = profiles
      .map((profile) => String(profile?.userId || '').trim())
      .filter(Boolean)
      .slice(0, 12);
    if (candidates.length === 0) return {};
    const results = await Promise.allSettled(
      candidates.map((userId) => getSocialMutuals(accessToken, userId)),
    );
    const updates: Record<string, any> = {};
    results.forEach((result, index) => {
      const userId = candidates[index];
      if (!userId) return;
      if (result.status === 'fulfilled' && result.value?.success) {
        updates[userId] = result.value;
      }
    });
    setSocialMutualsByUser((previous) => {
      return { ...previous, ...updates };
    });
    return updates;
  };

  const getConnectionFitScore = (profile: any, mutualMap?: Record<string, any>): number => {
    if (Number.isFinite(Number(profile?.connectionFit))) {
      return Number(profile.connectionFit);
    }
    const userId = String(profile?.userId || '').trim();
    const mutualSource = mutualMap || socialMutualsByUser;
    const mutual = userId ? mutualSource[userId] : null;
    if (mutual?.compatibility?.score) {
      return Number(mutual.compatibility.score);
    }
    const commonInterests = Number(profile?.commonInterests || 0);
    const qualityScore = Number(profile?.qualityScore || 0);
    return Math.min(99, Math.max(30, Math.round(40 + commonInterests * 12 + qualityScore * 0.4)));
  };

  const socialIcebreakers = [
    'One communication win I had this week...',
    'A boundary that helped me feel calmer today...',
    'Something I wish I understood earlier about relationships...',
    'A repair move that actually worked for me...',
  ];
  const isSocialFirstRun =
    Number(socialOverview?.myPosts || 0) === 0 &&
    Number(socialOverview?.myFollowing || 0) === 0;

  const getStateColor = (state: string) => {
    switch (state) {
      case 'Stable':
        return 'bg-green-500';
      case 'Elastic':
        return 'bg-yellow-500';
      case 'High Tension':
        return 'bg-red-500';
      default:
        return 'bg-neutral-500';
    }
  };

  const getForecastTrend = (forecastV2: { direction: 'stable' | 'rising' | 'variable'; probabilityRisePct: number; confidencePct: number }) => {
    const icon = forecastV2.direction === 'stable' ? Minus : forecastV2.direction === 'rising' ? TrendingUp : TrendingDown;
    const text = forecastV2.direction === 'stable' ? 'Stable' : forecastV2.direction === 'rising' ? 'Rising' : 'Variable';
    const color = forecastV2.direction === 'stable' ? 'text-green-600' : forecastV2.direction === 'rising' ? 'text-red-600' : 'text-yellow-600';
    return {
      icon,
      text,
      color,
      probabilityRisePct: forecastV2.probabilityRisePct,
      confidencePct: forecastV2.confidencePct,
    };
  };

  const switchRelationship = (relationshipId: string) => {
    const updated = relationships.map((r: any) => ({
      ...r,
      isActive: r.id === relationshipId,
    }));
    localStorage.setItem('tension_relationships', JSON.stringify(updated));
    localStorage.setItem('tension_active_relationship', relationshipId);
    setShowRelationshipMenu(false);
    // Reload data for new relationship
    window.location.reload();
  };

  const streakDays = safeNumber(safeLocalStorageGet('tension_streak'), 0);
  let weekCheckIns = 0;
  let todayCheckIns = 0;
  try {
    const listWeek = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_checkins', []);
    const forRel = activeRelationshipId
      ? listWeek.filter((c: any) => safeString(c?.relationshipId) === activeRelationshipId)
      : listWeek;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayStartMs = todayStart.getTime();
    weekCheckIns = forRel.filter((c: any) => {
      const t = c?.timestamp ? new Date(c.timestamp).getTime() : 0;
      return t >= weekAgo;
    }).length;
    todayCheckIns = forRel.filter((c: any) => {
      const t = c?.timestamp ? new Date(c.timestamp).getTime() : 0;
      return t >= todayStartMs;
    }).length;
  } catch {
    weekCheckIns = 0;
    todayCheckIns = 0;
  }

  const hasCoreActionToday = todayCheckIns > 0;
  const socialGateLocked = socialRollout.allowed && !hasCoreActionToday;

  useEffect(() => {
    if (!socialRollout.allowed) return;
    if (gateTransitionRef.current === socialGateLocked) return;
    gateTransitionRef.current = socialGateLocked;
    if (socialGateLocked) {
      analytics.track('home_social_gate_blocked', {
        reason: 'core_action_required',
        checkInsToday: todayCheckIns,
      });
      recordHomeCoexistenceEvent('gate_block');
      return;
    }
    const unlockCelebrationKey = 'tension_home_social_unlock_celebration_date';
    const todayKey = todayIsoKey();
    const alreadyCelebratedToday = safeLocalStorageGet(unlockCelebrationKey) === todayKey;
    if (!alreadyCelebratedToday) {
      setShowSocialUnlockCelebration(true);
      setShowHomeSocialPanel(true);
      try {
        localStorage.setItem(unlockCelebrationKey, todayKey);
      } catch {
        // noop
      }
      analytics.track('home_social_unlock_celebrated', {
        checkInsToday: todayCheckIns,
      });
    }
    analytics.track('home_social_gate_unlocked', {
      checkInsToday: todayCheckIns,
    });
  }, [socialRollout.allowed, socialGateLocked, todayCheckIns]);

  useEffect(() => {
    if (!showSocialUnlockCelebration) return;
    const timer = window.setTimeout(() => setShowSocialUnlockCelebration(false), 9000);
    return () => window.clearTimeout(timer);
  }, [showSocialUnlockCelebration]);

  const socialSupportPrompt =
    rti?.state === 'High Tension'
      ? 'I could use one practical de-escalation step that worked for you.'
      : rti?.state === 'Elastic'
        ? 'What is one small repair habit that helped your consistency?'
        : 'What is one habit that helps keep relationship momentum steady?';

  const supportCircles =
    rti?.state === 'High Tension'
      ? ['Repair Support', 'De-escalation', 'Boundary Reset']
      : rti?.state === 'Elastic'
        ? ['Consistency Boost', 'Communication Rhythm', 'Trust Rebuild']
        : ['Maintenance Habits', 'Appreciation Rituals', 'Long-term Stability'];
  const socialRelationshipLabel = activeRelationship?.name?.trim() || 'your relationship';
  const socialPanelTitle =
    rti?.state === 'High Tension'
      ? `Repair support for ${socialRelationshipLabel}`
      : rti?.state === 'Elastic'
        ? `Momentum support for ${socialRelationshipLabel}`
        : `Steady-state support for ${socialRelationshipLabel}`;
  const socialPanelSubtitle =
    rti?.state === 'High Tension'
      ? 'Use peers for concrete de-escalation ideas after your core check-in.'
      : rti?.state === 'Elastic'
        ? 'Turn today’s core insight into one social accountability action.'
        : 'Keep progress consistent with one lightweight support action.';
  const socialSuggestedFocus = loginSnapshot?.quickAdvice?.[0] || null;

  const isLowSignalPost = (post: any) => {
    const content = String(post?.content || '').trim();
    if (!content) return true;
    if (content.length < socialTuning.feedMinPostChars) return true;
    const words = content.split(/\s+/).filter(Boolean);
    if (words.length < 5) return true;
    const lowercase = content.toLowerCase();
    return ['hi', 'hey', 'anyone?', 'help', 'idk'].some(
      (token) => lowercase === token || lowercase.startsWith(`${token} `),
    );
  };

  const filteredSocialFeed = socialFeed.filter((post) => {
    if (!socialTuning.feedHideLowSignal) return true;
    if (socialQualityMode === 'balanced') {
      return String(post?.content || '').trim().length >= Math.max(8, socialTuning.feedMinPostChars - 8);
    }
    return !isLowSignalPost(post);
  });

  const weeklySupportRecap = (() => {
    const telemetry = readHomeCoexistenceTelemetry();
    const totals = {
      coreActions: 0,
      socialActions: 0,
      socialAfterCore: 0,
      panelExpands: 0,
      gateBlocks: 0,
    };
    for (let i = 0; i < 7; i += 1) {
      const key = dateKeyDaysAgo(i);
      const row = telemetry.byDay[key];
      if (!row) continue;
      totals.coreActions += Number(row.coreActions || 0);
      totals.socialActions += Number(row.socialActions || 0);
      totals.socialAfterCore += Number(row.socialActionsAfterCore || 0);
      totals.panelExpands += Number(row.socialPanelExpands || 0);
      totals.gateBlocks += Number(row.gateBlocks || 0);
    }
    const supportRate = totals.socialActions > 0 ? Math.round((totals.socialAfterCore / totals.socialActions) * 100) : 100;
    return { ...totals, supportRate };
  })();

  const socialMomentum = (() => {
    const goals = {
      follows: socialMomentumState.follows >= 1,
      comments: socialMomentumState.comments >= 1,
      posts: socialMomentumState.posts >= 1,
    };
    const completed = Number(goals.follows) + Number(goals.comments) + Number(goals.posts);
    const nextStep: 'follow' | 'comment' | 'post' | null = !goals.follows
      ? 'follow'
      : !goals.comments
        ? 'comment'
        : !goals.posts
          ? 'post'
          : null;
    return {
      goals,
      completed,
      nextStep,
      done: completed === 3,
    };
  })();

  const socialMomentumProgressPct = Math.round((socialMomentum.completed / 3) * 100);
  const socialMomentumNextLabel =
    socialMomentum.nextStep === 'follow'
      ? 'Follow one peer'
      : socialMomentum.nextStep === 'comment'
        ? 'Comment on one post'
        : socialMomentum.nextStep === 'post'
          ? 'Share one post'
          : 'Complete';
  const socialMomentumGuideLabel = socialMomentum.done
    ? 'Momentum complete'
    : socialMomentum.nextStep === 'follow'
      ? 'Guide: Follow'
      : socialMomentum.nextStep === 'comment'
        ? 'Guide: Comment'
        : 'Guide: Post';
  const hasCommentableFeed = filteredSocialFeed.length > 0;
  const momentumStepLabel = (step: 'follow' | 'comment' | 'post' | null) =>
    step === 'follow'
      ? 'Follow one peer'
      : step === 'comment'
        ? 'Comment on one post'
        : step === 'post'
          ? 'Share one post'
          : null;
  const nextMomentumStepAfter = (step: 'follow' | 'comment' | 'post' | null) =>
    step === 'follow' ? 'comment' : step === 'comment' ? 'post' : null;

  const runSocialMomentumNudge = () => {
    setSocialMomentumState((prev) => {
      const todayKey = todayIsoKey();
      const base =
        prev.dateKey === todayKey
          ? prev
          : { dateKey: todayKey, follows: 0, comments: 0, posts: 0, nudgesUsed: 0 };
      const next = { ...base, nudgesUsed: base.nudgesUsed + 1 };
      writeHomeSocialMomentumState(next);
      return next;
    });
    analytics.track('home_social_action_taken', {
      action: 'social_momentum_nudge',
      nextStep: socialMomentum.nextStep || 'complete',
      afterCore: hasCoreActionToday,
    });

    if (socialMomentum.nextStep === 'follow') {
      const circle = supportCircles[0] || 'Support';
      setShowHomeSocialPanel(true);
      setSocialFriendQuery(circle);
      void runFriendSearch(circle);
      toast.success('Step 1: follow one peer to start your support loop.');
      return;
    }
    if (socialMomentum.nextStep === 'comment') {
      if (!hasCommentableFeed) {
        const circle = supportCircles[0] || 'Support';
        setShowHomeSocialPanel(true);
        setSocialFriendQuery(circle);
        void runFriendSearch(circle);
        toast.success('No feed post available yet. Start by following one peer in your suggested circle.');
        return;
      }
      setShowHomeSocialPanel(true);
      const postId = String(filteredSocialFeed[0]?.id || '').trim();
      if (postId) {
        setSocialCommentDrafts((prev) => ({
          ...prev,
          [postId]: prev[postId] || 'Helpful insight - I am applying this today.',
        }));
      }
      toast.success('Step 2: add one supportive comment to build momentum.');
      return;
    }
    if (socialMomentum.nextStep === 'post') {
      setShowHomeSocialPanel(true);
      setSocialPostDraft((prev) => (prev.trim() ? prev : socialSupportPrompt));
      toast.success('Step 3: share one practical post to complete today\'s loop.');
      return;
    }
    toast.success('Momentum loop complete. Keep supporting others today.');
  };

  const runNextBestSocialAction = async () => {
    if (socialGateLocked) {
      toast.error('Complete one core action to unlock social actions.');
      return;
    }
    if (socialActionBusy) return;

    setShowHomeSocialPanel(true);
    setSocialActionBusy(true);
    try {
      if (socialMomentum.nextStep === 'follow') {
        let candidate = socialFriendResults[0] || socialSuggestions[0];
        if (!candidate) {
          const circle = supportCircles[0] || 'Support';
          setSocialFriendQuery(circle);
          const results = await runFriendSearch(circle);
          candidate = results[0];
        }
        const targetUserId = String(candidate?.userId || '').trim();
        if (!targetUserId) {
          toast.error('No suggested peer available yet. Try again in a moment.');
          return;
        }
        await handleFriendFollow(targetUserId, socialFriendResults[0] ? 'search' : 'suggestion', 1);
        const nextStep = nextMomentumStepAfter('follow');
        setSocialActionRecap({
          message: 'Done: followed one peer.',
          nextLabel: momentumStepLabel(nextStep),
        });
        toast.success('Next best action done: followed one peer.');
        return;
      }

      if (socialMomentum.nextStep === 'comment') {
        const postId = String(filteredSocialFeed[0]?.id || '').trim();
        if (!postId) {
          const circle = supportCircles[0] || 'Support';
          setSocialFriendQuery(circle);
          await runFriendSearch(circle);
          toast.success('No comment target available. Found peers to follow instead.');
          return;
        }
        const fallbackComment = 'This is helpful — I am trying this approach today.';
        const comment = (socialCommentDrafts[postId] || fallbackComment).trim();
        setSocialCommentDrafts((prev) => ({ ...prev, [postId]: comment }));
        await commentOnSocialPost(accessToken!, postId, comment);
        trackSocialAction('comment_create', hasCoreActionToday);
        setSocialMomentumState((prev) => {
          const todayKey = todayIsoKey();
          const base =
            prev.dateKey === todayKey
              ? prev
              : { dateKey: todayKey, follows: 0, comments: 0, posts: 0, nudgesUsed: 0 };
          const next = { ...base, comments: base.comments + 1 };
          writeHomeSocialMomentumState(next);
          return next;
        });
        markMissionCompletedIfActive('comment');
        setSocialCommentDrafts((prev) => ({ ...prev, [postId]: '' }));
        await loadHomeSocialData();
        const nextStep = nextMomentumStepAfter('comment');
        setSocialActionRecap({
          message: 'Done: added one supportive comment.',
          nextLabel: momentumStepLabel(nextStep),
        });
        toast.success('Next best action done: commented on one post.');
        return;
      }

      if (socialMomentum.nextStep === 'post') {
        const content = socialPostDraft.trim() || socialSupportPrompt;
        if (socialTuning.feedHideLowSignal && content.length < socialTuning.feedMinPostChars) {
          toast.error(`Add more context (${socialTuning.feedMinPostChars}+ chars) for a higher-quality post.`);
          return;
        }
        await createSocialPost(accessToken!, { content, tags: ['home', 'community'] });
        trackSocialAction('post_create', hasCoreActionToday);
        setSocialMomentumState((prev) => {
          const todayKey = todayIsoKey();
          const base =
            prev.dateKey === todayKey
              ? prev
              : { dateKey: todayKey, follows: 0, comments: 0, posts: 0, nudgesUsed: 0 };
          const next = { ...base, posts: base.posts + 1 };
          writeHomeSocialMomentumState(next);
          return next;
        });
        markMissionCompletedIfActive('post');
        setSocialPostDraft('');
        await loadHomeSocialData();
        setSocialActionRecap({
          message: 'Done: shared one practical post.',
          nextLabel: null,
        });
        toast.success('Next best action done: posted one update.');
        return;
      }

      setSocialActionRecap({
        message: 'Momentum loop complete.',
        nextLabel: null,
      });
      toast.success('Momentum loop complete. No further action needed.');
    } catch (error: any) {
      toast.error(error?.message || 'Could not run next action');
    } finally {
      setSocialActionBusy(false);
    }
  };

  const redirectToSocialUnlock = (source: string) => {
    trackCoreAction(`unlock_social_from_${source}`);
    toast.message('Complete a quick pulse to unlock social tools.');
    navigate('/quick-check-in');
  };

  const dailySupportMission = (() => {
    if (!socialTuning.weeklyMissionEnabled) return null;
    if (!hasCoreActionToday) {
      return {
        title: 'Complete one core action',
        description: 'Run quick pulse first to unlock today’s support mission.',
        cta: 'Complete quick pulse',
        run: () => {
          trackCoreAction('daily_support_mission_unlock_core');
          navigate('/quick-check-in');
        },
      };
    }
    const suggestedCircle = supportCircles[0] || 'Support';
    return {
      title: 'Daily support mission',
      description: `Share one practical insight and connect in ${suggestedCircle}.`,
      cta: 'Run mission now',
      run: () => {
        writeHomeMissionState({
          dateKey: todayIsoKey(),
          started: true,
          completed: false,
          startCircle: suggestedCircle,
        });
        analytics.track('home_social_mission_started', {
          circle: suggestedCircle,
          qualityMode: socialQualityMode,
          afterCore: hasCoreActionToday,
        });
        recordHomeSocialConversionEvent({ kind: 'mission_started' });
        setShowHomeSocialPanel(true);
        setSocialPostDraft(socialSupportPrompt);
        void applySupportCircle(suggestedCircle);
        trackSocialAction('daily_support_mission_started', true);
      },
    };
  })();

  useEffect(() => {
    if (!showHomeSocialPanel || socialGateLocked) return;
    const key = `tension_home_weekly_recap_viewed:${todayIsoKey()}`;
    if (safeLocalStorageGet(key) === '1') return;
    try {
      localStorage.setItem(key, '1');
    } catch {
      // noop
    }
    analytics.track('home_social_weekly_recap_viewed', {
      coreActions: weeklySupportRecap.coreActions,
      socialActions: weeklySupportRecap.socialActions,
      supportRate: weeklySupportRecap.supportRate,
      qualityMode: socialQualityMode,
    });
    recordHomeSocialConversionEvent({ kind: 'weekly_recap_viewed' });
  }, [
    showHomeSocialPanel,
    socialGateLocked,
    weeklySupportRecap.coreActions,
    weeklySupportRecap.socialActions,
    weeklySupportRecap.supportRate,
    socialQualityMode,
  ]);

  useEffect(() => {
    if (!showHomeSocialPanel || socialGateLocked) return;
    const suggestedCircle = supportCircles[0] || 'Support';
    const bootstrapKey = `tension_home_social_bootstrap:${todayIsoKey()}:${suggestedCircle}`;

    if (!socialFriendQuery.trim()) {
      setSocialFriendQuery(suggestedCircle);
    }
    if (!socialPostDraft.trim()) {
      setSocialPostDraft(socialSupportPrompt);
    }
    if (!socialFriendResults.length && !socialSearching && safeLocalStorageGet(bootstrapKey) !== '1') {
      try {
        localStorage.setItem(bootstrapKey, '1');
      } catch {
        // noop
      }
      void runFriendSearch(suggestedCircle);
      analytics.track('home_social_panel_bootstrapped', {
        circle: suggestedCircle,
        afterCore: hasCoreActionToday,
      });
    }
  }, [
    showHomeSocialPanel,
    socialGateLocked,
    supportCircles,
    socialFriendQuery,
    socialSupportPrompt,
    socialPostDraft,
    socialFriendResults.length,
    socialSearching,
    hasCoreActionToday,
  ]);

  useEffect(() => {
    if (socialGateLocked) {
      setSocialActionRecap(null);
    }
  }, [socialGateLocked]);

  useEffect(() => {
    if (!socialRollout.allowed) return;
    const key = `tension_home_assist_impression:${todayIsoKey()}:${communityAssistVariant}`;
    if (safeLocalStorageGet(key) === '1') return;
    try {
      localStorage.setItem(key, '1');
    } catch {
      // noop
    }
    analytics.track('home_social_action_taken', {
      action: 'community_assist_impression',
      afterCore: hasCoreActionToday,
      surface: 'home',
      variant: communityAssistVariant,
    });
    recordHomeSocialConversionEvent({
      kind: 'assist_impression',
      variant: communityAssistVariant,
    });
  }, [socialRollout.allowed, communityAssistVariant, hasCoreActionToday]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const metaName = user?.user_metadata?.name;
  const displayName =
    typeof metaName === 'string' && metaName.trim()
      ? metaName.trim().split(/\s+/)[0]!
      : user?.email
        ? user.email.split('@')[0]!
        : null;

  const saveTenSecondCheckIn = (feeling: number, contextTag: string) => {
    if (!activeRelationshipId) {
      toast.error('Select a relationship first');
      navigate('/relationships');
      return;
    }
    const now = new Date().toISOString();
    const rtiScore = Math.round(100 - feeling);
    const components = {
      initiation: feeling,
      latency: feeling,
      emotionalDensity: 100 - feeling,
      availability: feeling,
      withdrawal: 100 - feeling,
    };
    const entry = {
      relationshipId: activeRelationshipId,
      relationshipName: activeRelationship?.name || 'Unknown',
      date: now,
      timestamp: now,
      components,
      emotionalStates: [feeling],
      emotionalAverage: feeling,
      triggers: contextTag ? [contextTag] : [],
      rtiScore,
      quickCheckIn: true,
      source: 'home-10s',
    };
    try {
      const checkins = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_checkins', []);
      checkins.push(entry);
      localStorage.setItem('tension_checkins', JSON.stringify(checkins));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
      }
      trackCoreAction('ten_second_checkin_saved');
      toast.success('Saved in 10 seconds');
      fetchData();
    } catch {
      toast.error('Could not save check-in');
    }
  };

  const missedRecovery = (() => {
    try {
      const allCheckins = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_checkins', []);
      const checkins = allCheckins.filter(
        (c: any) => !activeRelationshipId || safeString(c?.relationshipId) === activeRelationshipId,
      );
      if (checkins.length === 0) return { show: false, days: 0 };
      const latest = checkins[checkins.length - 1] as Record<string, unknown> | undefined;
      const latestTimestamp = typeof latest?.timestamp === 'string' ? latest.timestamp : '';
      const lastTime = latestTimestamp ? new Date(latestTimestamp).getTime() : 0;
      const days = Math.floor((Date.now() - lastTime) / (1000 * 60 * 60 * 24));
      return { show: days >= 2, days };
    } catch {
      return { show: false, days: 0 };
    }
  })();

  const counterfactual = relationshipIntel?.projectCounterfactual(counterfactualStyle) ?? null;
  const counterfactualScript = relationshipIntel?.scriptForStyle(counterfactualStyle) ?? null;
  // Free core is always interactive; variants now tune depth and adaptive layers.
  const riInteractiveEnabled = Boolean(relationshipIntel && isCouplesMode);
  const riAdaptiveEnabled = isPremium || riVariant === 'adaptive_v2';
  const riMicroScenarioConfig = RI_MICRO_SCENARIOS.find((scenario) => scenario.id === riMicroScenario) || RI_MICRO_SCENARIOS[1];
  const riMicroMomentumLabel = riMicroEnergy >= 75 ? 'high intent' : riMicroEnergy >= 50 ? 'balanced intent' : 'low pressure';
  const riMicroMoveSource = relationshipIntel?.twin.nextBestMove || riMicroScenarioConfig.action;
  const riMicroMoveWords = riMicroMoveSource.split(' ');
  const riMicroVisibleWords = Math.min(riMicroMoveWords.length, 8 + riMicroRevealStep * 6);
  const riMicroVisibleMove = riMicroMoveWords.slice(0, riMicroVisibleWords).join(' ');
  const riMicroDone = riMicroVisibleWords >= riMicroMoveWords.length;

  const trackRiEvent = (event: Parameters<typeof analytics.track>[0], properties?: Record<string, unknown>) => {
    analytics.track(event, {
      variant: riVariant,
      premium: isPremium,
      ...(properties || {}),
    });
    if (riRolloutEligible) {
      if (event === 'ri_interaction_started') {
        recordHomeRiExperimentMetric(riVariant, 'interactions');
        setRiHealthRevision((value) => value + 1);
      } else if (event === 'ri_interaction_completed') {
        recordHomeRiExperimentMetric(riVariant, 'completions');
        setRiHealthRevision((value) => value + 1);
      } else if (event === 'ri_upgrade_prompt_clicked') {
        recordHomeRiExperimentMetric(riVariant, 'upgradeClicks');
        setRiHealthRevision((value) => value + 1);
      }
    }
  };

  const riDebugVisible = isAdmin || safeLocalStorageGet('tension_debug_toasts') === '1';
  const riExperimentHealth = useMemo(
    () => getHomeRiExperimentHealth(riExperimentConfig),
    [riExperimentConfig, riHealthRevision],
  );
  const riRolloutRecommendation = useMemo(
    () => getHomeRiRolloutRecommendation(riExperimentConfig, riExperimentHealth),
    [riExperimentConfig, riExperimentHealth],
  );
  const riRolloutCooldownRemainingMs = useMemo(
    () => getHomeRiRolloutCooldownRemainingMs(riCooldownClockMs),
    [riCooldownClockMs, riExperimentConfig.updatedAt],
  );
  const riRolloutLocked = riRolloutCooldownRemainingMs > 0;

  useEffect(() => {
    if (!riDebugVisible) return;
    const timer = window.setInterval(() => {
      setRiCooldownClockMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [riDebugVisible]);

  const handleResetRiExperimentStats = () => {
    if (riResetBusy) return;
    const confirmed = window.confirm(
      'Reset Relationship Intelligence experiment stats and assignment logs on this device?',
    );
    if (!confirmed) return;
    setRiResetBusy(true);
    try {
      resetHomeRiExperimentStats();
      setRiHealthRevision((value) => value + 1);
      toast.success('RI experiment stats reset on this device.');
    } catch {
      toast.error('Could not reset RI experiment stats.');
    } finally {
      setRiResetBusy(false);
    }
  };

  const applyRiRolloutPreset = (preset: 'rollback' | 10 | 20 | 35 | 50) => {
    if (preset !== 'rollback' && isHomeRiRolloutChangeLocked()) {
      const remainingMs = getHomeRiRolloutCooldownRemainingMs();
      const remainingMinutes = Math.ceil(remainingMs / 60000);
      toast.error(`Rollout cooldown active. Try again in about ${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}.`);
      return;
    }
    const nextConfig =
      preset === 'rollback'
        ? {
            ...riExperimentConfig,
            enabled: false,
            rolloutPct: 0,
            forcedVariant: null,
            updatedAt: new Date().toISOString(),
          }
        : {
            ...riExperimentConfig,
            enabled: true,
            rolloutPct: preset,
            forcedVariant: null,
            updatedAt: new Date().toISOString(),
          };
    writeHomeRiExperimentConfig(nextConfig);
    markHomeRiRolloutChange();
    setRiExperimentConfig(nextConfig);
    setRiCooldownClockMs(Date.now());
    analytics.track('ri_cta_clicked', {
      target: preset === 'rollback' ? 'ri_rollout_preset_rollback' : `ri_rollout_preset_${preset}`,
      variant: riVariant,
      premium: isPremium,
    });
    toast.success(
      preset === 'rollback'
        ? 'RI rollout disabled (rollback preset applied).'
        : `RI rollout preset applied: ${preset}%.`,
    );
  };

  const applyRecommendedRiRollout = () => {
    const rec = riRolloutRecommendation;
    if (rec.action === 'hold') {
      toast.message('Recommendation is hold. No rollout change applied.');
      return;
    }
    if (rec.action === 'rollback') {
      applyRiRolloutPreset('rollback');
      return;
    }
    const target = rec.targetRolloutPct as 10 | 20 | 35 | 50;
    applyRiRolloutPreset(target);
  };

  const toggleRiMissionStep = (stepId: string) => {
    setRiMissionState((prev) => {
      const completedSet = new Set(prev.completedStepIds);
      if (completedSet.has(stepId)) completedSet.delete(stepId);
      else completedSet.add(stepId);
      return {
        dateKey: todayIsoKey(),
        completedStepIds: [...completedSet],
        updatedAt: Date.now(),
      };
    });
  };

  const resetRiMission = () =>
    setRiMissionState({
      dateKey: todayIsoKey(),
      completedStepIds: [],
      updatedAt: Date.now(),
    });

  const jumpToPriorityRiTool = (sectionId: string) => {
    trackRiEvent('ri_cta_clicked', { target: 'priority_tool', sectionId });
    setHomeSections((prev) => ({
      ...prev,
      tools: true,
    }));
    toast.message('Priority tools are available in Tools and Practice.');
  };

  useEffect(() => {
    if (!relationshipIntel || !isCouplesMode || !homeSections.insights || riSectionViewedRef.current) return;
    riSectionViewedRef.current = true;
    trackRiEvent('ri_section_viewed', {
      adaptiveEnabled: riAdaptiveEnabled,
      interactiveEnabled: riInteractiveEnabled,
    });
  }, [homeSections.insights, isCouplesMode, relationshipIntel, riAdaptiveEnabled, riInteractiveEnabled]);

  useEffect(() => {
    if (!location.hash) return;
    if (location.hash === '#relationship-intelligence') {
      setHomeSections((prev) => ({ ...prev, insights: true }));
      window.setTimeout(() => {
        document.getElementById('relationship-intelligence')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }
    if (location.hash === '#tools-and-practice') {
      setHomeSections((prev) => ({ ...prev, tools: true }));
      window.setTimeout(() => {
        document.getElementById('tools-and-practice')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!relationshipIntel) return;
    const stepIds = relationshipIntel.repairPlan.steps.map((step) => step.id);
    if (stepIds.length === 0) return;
    const allCompleted = stepIds.every((id) => riMissionState.completedStepIds.includes(id));
    if (!allCompleted) return;
    const completionKey = `tension_ri_mission_complete:${todayIsoKey()}`;
    if (safeLocalStorageGet(completionKey) === '1') return;
    try {
      localStorage.setItem(completionKey, '1');
    } catch {
      // noop
    }
    trackRiEvent('ri_interaction_completed', {
      completedSteps: stepIds.length,
      surface: 'repair_mission',
    });
  }, [relationshipIntel, riMissionState.completedStepIds]);

  const weeklyLoop = (() => {
    try {
      const allCheckins = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_checkins', []);
      const checkins = allCheckins.filter(
        (c: any) => !activeRelationshipId || safeString(c?.relationshipId) === activeRelationshipId,
      );
      const now = Date.now();
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      const recent = checkins.filter((c: any) => new Date(c.timestamp || c.date).getTime() >= now - weekMs);
      const prior = checkins.filter((c: any) => {
        const t = new Date(c.timestamp || c.date).getTime();
        return t < now - weekMs && t >= now - 2 * weekMs;
      });
      const avg = (list: any[]) =>
        list.length > 0 ? Math.round(list.reduce((sum, c) => sum + safeNumber(c.rtiScore, 0), 0) / list.length) : null;
      const recentAvg = avg(recent);
      const priorAvg = avg(prior);
      if (recentAvg == null || priorAvg == null) return null;
      return {
        recentAvg,
        priorAvg,
        delta: priorAvg - recentAvg,
      };
    } catch {
      return null;
    }
  })();

  const heroEyebrow = useMemo(() => {
    const options = rti
      ? ['Your relationship HQ', 'Live check-in command center', 'RTI and chat in one flow']
      : ['Your relationship HQ', 'Start your daily baseline', 'Check-in first, then coaching'];
    const seed = `${todayIsoKey()}|${activeRelationshipId || 'none'}|${weekCheckIns}|${streakDays}|${rti?.score ?? 'na'}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    return options[Math.abs(hash) % options.length] || options[0]!;
  }, [activeRelationshipId, rti, streakDays, weekCheckIns]);

  const heroLead = useMemo(() => {
    if (!rti) {
      return 'Run a quick check-in to unlock your RTI score, streaks, and personalized coaching.';
    }
    const options: Array<string> = [];
    const relationshipLabel = activeRelationship?.name || 'your relationship';
    const trend =
      typeof rti.score === 'number' && typeof rti.previousScore === 'number'
        ? rti.score > rti.previousScore
          ? 'up'
          : rti.score < rti.previousScore
            ? 'down'
            : 'stable'
        : null;
    if (trend === 'down') {
      options.push(`RTI is easing for ${relationshipLabel}; keep today's chat and check-in aligned to protect momentum.`);
    } else if (trend === 'up') {
      options.push(`RTI is trending higher for ${relationshipLabel}; one clean check-in plus one calm message can interrupt escalation.`);
    } else {
      options.push(`RTI is steady for ${relationshipLabel}; today is about consistency through one check-in and one grounded response.`);
    }
    if (weekCheckIns > 0) {
      options.push(`${weekCheckIns} check-in${weekCheckIns === 1 ? '' : 's'} this week are improving signal quality for better real-time coaching.`);
    }
    if (streakDays > 0) {
      options.push(`You are on a ${streakDays}-day check-in streak. Keep the streak and let RTI calibrate with today's moment.`);
    }
    if (loginSnapshot?.riskLevel === 'high') {
      options.push('Risk is elevated right now, so prioritize a short check-in first, then use the live chat to draft one low-reactivity message.');
    }
    if (missedRecovery.show) {
      options.push(`It has been ${missedRecovery.days} day${missedRecovery.days === 1 ? '' : 's'} since your last check-in. A quick pulse now will refresh RTI context.`);
    }
    if (weeklyLoop && Math.abs(weeklyLoop.delta) >= 2) {
      options.push(
        weeklyLoop.delta > 0
          ? `RTI improved ${Math.abs(weeklyLoop.delta)} points week over week; keep reinforcing the behaviors from your recent check-ins.`
          : `RTI slipped ${Math.abs(weeklyLoop.delta)} points week over week; use today's check-in to identify the trigger pattern and respond early.`,
      );
    }
    if (!hasCoreActionToday) {
      options.push('Core action is still pending. Complete one quick check-in, then run one de-escalation message in live chat.');
    }
    const seed = `${todayIsoKey()}|${activeRelationshipId || 'none'}|${weekCheckIns}|${streakDays}|${rti.score}|${loginSnapshot?.riskLevel || 'na'}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    return options[Math.abs(hash) % options.length] || `You're tracking ${relationshipLabel} — tiny daily wins compound.`;
  }, [
    activeRelationship?.name,
    activeRelationshipId,
    hasCoreActionToday,
    loginSnapshot?.riskLevel,
    missedRecovery.days,
    missedRecovery.show,
    rti,
    streakDays,
    weekCheckIns,
    weeklyLoop,
  ]);

  const toggleHomeSection = (key: HomeSectionKey) => {
    setHomeSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const quickTensionPatternSummary = Object.entries(
    (tensionQuickResponse?.memory?.patternCounts || {}) as Record<string, number>,
  )
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 3)
    .map(([name]) => name.replace(/_/g, ' '));

  const tensionQuickEmotionState: TensionEmotionState = useMemo(() => {
    const mappedEmotion: TensionEmotionState['userEmotion'] =
      tensionQuickEmotion === 'hurt'
        ? 'hurt'
        : tensionQuickEmotion === 'frustrated'
          ? 'defensive'
          : tensionQuickEmotion === 'confused'
            ? 'confused'
            : 'calm';
    const intensity =
      tensionQuickEmotion === 'hurt'
        ? 84
        : tensionQuickEmotion === 'frustrated'
          ? 78
          : tensionQuickEmotion === 'overwhelmed'
            ? 72
            : tensionQuickEmotion === 'anxious'
              ? 68
              : 44;
    const relationshipRisk = tensionQuickLiveSignal?.riskLevel || (intensity >= 80 ? 'high' : intensity >= 64 ? 'medium' : 'low');
    return {
      userEmotion: mappedEmotion,
      intensity,
      relationshipRisk,
      clarityNeed: intensity >= 64 || relationshipRisk !== 'low' ? 'high' : 'low',
    };
  }, [tensionQuickEmotion, tensionQuickLiveSignal?.riskLevel]);

  const mapRealtimePhaseLabel = (raw: string): string => {
    const value = raw.trim().toLowerCase();
    if (!value || value === 'idle') return 'thinking...';
    if (value.includes('listen')) return 'thinking...';
    if (value.includes('context') || value.includes('analy')) return 'reading context...';
    if (value.includes('generat')) return 'understanding tone...';
    if (value.includes('respond') || value.includes('refin')) return 'composing message...';
    if (value.includes('ready') || value.includes('complete')) return 'ready';
    return `${value}...`;
  };

  const buildAssistantTurnText = (
    payload: Pick<TensionRespondPayload, 'response'> | null,
    partial?: TensionStreamPartialEvent | null,
    phase?: string,
  ): string => {
    const insight = payload?.response?.insight || partial?.insight;
    const reframe = payload?.response?.reframe || partial?.reframe || null;
    if (insight) return [insight, reframe].filter(Boolean).join('\n');
    const firstSuggested = payload?.response?.sharing?.sendThis
      || payload?.response?.suggestedMessages?.[0]
      || partial?.suggestedMessages?.[0];
    if (firstSuggested) return firstSuggested;
    return phase ? mapRealtimePhaseLabel(phase) : 'Typing...';
  };

  const inferRealtimeInputs = (
    situationText: string,
  ): { contextType: TensionContextType; emotion: TensionEmotion } => {
    const text = situationText.trim().toLowerCase();
    const contextType: TensionContextType =
      /tone|harsh|rude|snappy|attitude/.test(text)
        ? 'tone_issue'
        : /distance|pull away|quiet|silent|ignored|ignoring/.test(text)
          ? 'emotional_distance'
          : /argu|fight|yell|blow up|escalat/.test(text)
            ? 'argument_starting'
            : /misunder|landed wrong|crossed wires|took it wrong/.test(text)
              ? 'misunderstanding'
              : 'other';
    const emotion: TensionEmotion =
      /hurt|pain|sad|rejected/.test(text)
        ? 'hurt'
        : /frustrat|angry|mad|annoyed/.test(text)
          ? 'frustrated'
          : /overwhelm|drained|exhausted|too much/.test(text)
            ? 'overwhelmed'
            : /anxious|panic|nervous|worried/.test(text)
              ? 'anxious'
              : 'confused';
    return { contextType, emotion };
  };

  const buildRealtimeTurnSignature = (
    situationText: string,
  ): string => `${situationText.trim().toLowerCase()}`;

  const calculateRealtimeDelayMs = (input: string, emotion: TensionEmotion): number => {
    const clean = input.trim();
    const simple = clean.length < 56;
    if (emotion === 'hurt') return simple ? 420 : 760;
    if (emotion === 'frustrated' || emotion === 'overwhelmed') return simple ? 300 : 560;
    if (emotion === 'anxious') return simple ? 220 : 420;
    return simple ? 80 : 200;
  };

  const runRealtimeTension = async (source: 'auto' | 'manual' = 'manual') => {
    const cleanSituation = tensionQuickSituation.trim();
    if (!cleanSituation) {
      toast.error('Describe what just happened first.');
      return;
    }
    const inferred = inferRealtimeInputs(cleanSituation);
    setTensionQuickContext(inferred.contextType);
    setTensionQuickEmotion(inferred.emotion);
    const signature = buildRealtimeTurnSignature(cleanSituation);
    if (
      source === 'auto' &&
      (tensionQuickBusy ||
        tensionRealtimeInFlightSignatureRef.current === signature ||
        tensionRealtimeLastAutoSignatureRef.current === signature)
    ) {
      return;
    }
    const runToken = ++tensionRealtimeRunTokenRef.current;
    tensionRealtimeInFlightSignatureRef.current = signature;
    if (source === 'auto') {
      tensionRealtimeLastAutoSignatureRef.current = signature;
    }
    const userTurnId = crypto.randomUUID();
    const assistantTurnId = crypto.randomUUID();
    setTensionQuickTurns((prev) => [
      ...prev.slice(-39),
      { id: userTurnId, role: 'user', text: cleanSituation, status: 'final' },
      { id: assistantTurnId, role: 'assistant', text: 'Typing...', status: 'streaming' },
    ]);
    setTensionQuickSituation('');
    setTensionQuickBusy(true);
    setTensionQuickPhaseLabel('thinking...');
    setTensionQuickPartial(null);
    try {
      const pacingDelay = calculateRealtimeDelayMs(
        cleanSituation,
        inferred.emotion,
      );
      if (pacingDelay > 0) {
        await new Promise<void>((resolve) => {
          window.setTimeout(() => resolve(), pacingDelay);
        });
      }

      const response = await requestTensionResponseStream({
        sessionId: tensionQuickSessionId,
        input: {
          situationText: cleanSituation,
          contextType: inferred.contextType,
          emotion: inferred.emotion,
          relationshipMode: activeRelationship?.type || 'relationship',
          personalization: {
            relationshipName: activeRelationship?.name || null,
            relationshipType: activeRelationship?.type || null,
            rtiScore: typeof rti?.score === 'number' ? rti.score : null,
            checkInStreak: streakDays,
            checkInsLast7d: weekCheckIns,
            rtiTrend:
              typeof rti?.score === 'number' && typeof rti?.previousScore === 'number'
                ? rti.score > rti.previousScore
                  ? 'up'
                  : rti.score < rti.previousScore
                    ? 'down'
                    : 'stable'
                : null,
            recentRiskLevel: loginSnapshot?.riskLevel || null,
            lastInsightSummary: tensionQuickResponse?.memory?.lastInsightSummary || loginSnapshot?.summary || null,
            frequentPatterns: quickTensionPatternSummary,
            userVoiceHint: 'calm, clear, emotionally warm, non-robotic',
          },
        },
        options: { stream: true },
      }, {
        onState: (event) => {
          if (runToken !== tensionRealtimeRunTokenRef.current) return;
          setTensionQuickPhaseLabel(mapRealtimePhaseLabel(event.label));
          setTensionQuickTurns((prev) => prev.map((turn) =>
            turn.id === assistantTurnId && turn.status === 'streaming'
              ? { ...turn, text: buildAssistantTurnText(null, null, event.label) }
              : turn
          ));
        },
        onPartial: (event) => {
          if (runToken !== tensionRealtimeRunTokenRef.current) return;
          setTensionQuickPartial(event);
          setTensionQuickTurns((prev) => prev.map((turn) =>
            turn.id === assistantTurnId && turn.status === 'streaming'
              ? { ...turn, text: buildAssistantTurnText(null, event) }
              : turn
          ));
        },
      });
      if (runToken !== tensionRealtimeRunTokenRef.current) return;
      setTensionQuickResponse(response);
      setTensionQuickLiveSignal({
        classification: response.context.classification,
        riskLevel: response.context.riskLevel,
      });
      setTensionQuickPhaseLabel('ready');
      setTensionQuickTurns((prev) => prev.map((turn) =>
        turn.id === assistantTurnId
          ? { ...turn, text: buildAssistantTurnText(response), status: 'final' }
          : turn
      ));
      analytics.track('home_tension_realtime_generated', {
        contextType: inferred.contextType,
        emotion: inferred.emotion,
        classification: response.context.classification,
        riskLevel: response.context.riskLevel,
      });
    } catch (error) {
      console.error('home stream failed; falling back to non-stream', error);
      try {
        const response = await requestTensionResponse({
          sessionId: tensionQuickSessionId,
          input: {
            situationText: cleanSituation,
            contextType: inferred.contextType,
            emotion: inferred.emotion,
            relationshipMode: activeRelationship?.type || 'relationship',
            personalization: {
              relationshipName: activeRelationship?.name || null,
              relationshipType: activeRelationship?.type || null,
              rtiScore: typeof rti?.score === 'number' ? rti.score : null,
              checkInStreak: streakDays,
              checkInsLast7d: weekCheckIns,
              rtiTrend:
                typeof rti?.score === 'number' && typeof rti?.previousScore === 'number'
                  ? rti.score > rti.previousScore
                    ? 'up'
                    : rti.score < rti.previousScore
                      ? 'down'
                      : 'stable'
                  : null,
              recentRiskLevel: loginSnapshot?.riskLevel || null,
              lastInsightSummary: tensionQuickResponse?.memory?.lastInsightSummary || loginSnapshot?.summary || null,
              frequentPatterns: quickTensionPatternSummary,
              userVoiceHint: 'calm, clear, emotionally warm, non-robotic',
            },
          },
          options: { stream: false },
        });
        if (runToken !== tensionRealtimeRunTokenRef.current) return;
        setTensionQuickResponse(response);
        setTensionQuickLiveSignal({
          classification: response.context.classification,
          riskLevel: response.context.riskLevel,
        });
        setTensionQuickPhaseLabel('ready');
        setTensionQuickTurns((prev) => prev.map((turn) =>
          turn.id === assistantTurnId
            ? { ...turn, text: buildAssistantTurnText(response), status: 'final' }
            : turn
        ));
      } catch {
        if (runToken !== tensionRealtimeRunTokenRef.current) return;
        setTensionQuickTurns((prev) => prev.map((turn) =>
          turn.id === assistantTurnId
            ? { ...turn, text: 'I could not complete that reply. Try sending again.', status: 'final' }
            : turn
        ));
        toast.error('Could not generate response right now. Try again in a moment.');
      }
    } finally {
      if (runToken === tensionRealtimeRunTokenRef.current) {
        setTensionQuickBusy(false);
      }
      if (tensionRealtimeInFlightSignatureRef.current === signature) {
        tensionRealtimeInFlightSignatureRef.current = null;
      }
    }
  };

  const copyRealtimeMessage = async () => {
    const primary = tensionQuickResponse?.response?.sharing?.sendThis || tensionQuickResponse?.response?.suggestedMessages?.[0];
    if (!primary) {
      toast.error('Generate a response first.');
      return;
    }
    const ok = await copyToClipboard(primary);
    if (ok) toast.success('Copied send-ready message.');
    else toast.error('Copy failed. Try again.');
  };

  const shareRealtimeMoment = async () => {
    const quote = tensionQuickResponse?.response?.sharing?.shareMomentQuote || tensionQuickResponse?.response?.suggestedMessages?.[0];
    if (!quote) {
      toast.error('Generate a response first.');
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'TENSION moment',
          text: quote,
        });
        toast.success('Shared successfully.');
        return;
      } catch {
        // fallback below
      }
    }
    const ok = await copyToClipboard(quote);
    if (ok) toast.success('Copied share moment text.');
    else toast.error('Share unavailable right now.');
  };

  const applyRealtimeDynamicOption = (option: { id: string; label: string; starter: string }) => {
    const text = String(option?.starter || '').trim();
    if (!text) return;
    setTensionQuickSituation(text.slice(0, 280));
    setTensionQuickPhaseLabel('thinking...');
    void fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/tension/options/usage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          sessionId: tensionQuickSessionId,
          optionId: option.id,
          label: option.label,
        }),
      },
    ).catch(() => undefined);
    toast.success('Starter loaded. Edit or send when ready.');
  };

  useEffect(() => {
    const clean = tensionQuickSituation.trim();
    const inferred = inferRealtimeInputs(clean);
    if (clean) {
      setTensionQuickContext(inferred.contextType);
      setTensionQuickEmotion(inferred.emotion);
    }
    const signature = buildRealtimeTurnSignature(clean);
    if (!clean) {
      setTensionQuickPhaseLabel('idle');
      setTensionQuickLiveSignal(null);
      setTensionQuickPartial(null);
      tensionRealtimeDraftSignatureRef.current = '';
      tensionRealtimeLastAutoSignatureRef.current = '';
      tensionRealtimeInFlightSignatureRef.current = null;
      return;
    }
    const draftChanged = tensionRealtimeDraftSignatureRef.current !== signature;
    if (draftChanged) {
      tensionRealtimeDraftSignatureRef.current = signature;
      setTensionQuickPhaseLabel('thinking...');
      setTensionQuickPartial(null);
    }
    const contextTimer = window.setTimeout(() => {
      const requestId = tensionRealtimeGuardRef.current.next();
      void updateTensionLiveSession({
        sessionId: tensionQuickSessionId,
        input: {
          situationText: clean,
          contextType: inferred.contextType,
          emotion: inferred.emotion,
          relationshipMode: activeRelationship?.type || 'relationship',
        },
        options: { stream: false },
      })
        .then((payload) => {
          if (!tensionRealtimeGuardRef.current.isCurrent(requestId)) return;
          if (tensionRealtimeDraftSignatureRef.current !== signature) return;
          setTensionQuickLiveSignal({
            classification: payload.live.classification,
            riskLevel: payload.live.riskLevel,
          });
          setTensionQuickPhaseLabel('reading context...');
        })
        .catch(() => undefined);
    }, 180);

    return () => {
      window.clearTimeout(contextTimer);
    };
  }, [
    tensionQuickSituation,
    tensionQuickSessionId,
    activeRelationship?.type,
  ]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] tension-page-bg">
        <div className="max-w-2xl mx-auto px-4 pt-[max(0.25rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] space-y-4 motion-safe:animate-pulse sm:px-6 sm:pt-4 sm:pb-8">
          <div className="h-32 rounded-3xl bg-violet-200/50 dark:bg-violet-900/40" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-neutral-200/70 dark:bg-neutral-800/70" />
            ))}
          </div>
          <div className="h-56 rounded-2xl bg-neutral-200/60 dark:bg-neutral-800/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] tension-page-bg">
      <div
        className={`max-w-2xl mx-auto px-4 sm:px-6 pt-[max(0.25rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:pt-3 sm:pb-8 space-y-6 sm:space-y-8 ${
          canUseEnhancedMotion ? 'motion-safe:animate-fadeIn' : ''
        }`}
      >
        {/* Notification Status Banner */}
        <NamedHomeErrorBoundary name="NotificationStatusBanner">
          <NotificationStatusBanner />
        </NamedHomeErrorBoundary>

        <section
          className="rounded-3xl border border-violet-200/70 dark:border-violet-800/50 bg-gradient-to-br from-violet-600/12 via-fuchsia-500/8 to-transparent p-6 sm:p-7 shadow-sm dark:shadow-none dark:bg-neutral-900/40"
          aria-labelledby="home-welcome-heading"
        >
          <div className="grid gap-4">
            <div className="min-w-[14rem] w-full max-w-none">
              <p className="text-sm font-medium text-violet-600 dark:text-violet-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
                {heroEyebrow}
              </p>
              <h1
                id="home-welcome-heading"
                className="mt-1 w-full max-w-none text-2xl font-semibold tracking-tight text-neutral-900 break-normal text-pretty sm:text-3xl dark:text-neutral-50"
              >
                {greeting}
                {displayName ? `, ${displayName}` : ''}
              </h1>
              <p className="mt-2 w-full max-w-none text-sm leading-relaxed text-neutral-600 text-pretty dark:text-neutral-400">
                {heroLead}
              </p>
            </div>
            <div className="flex w-full flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  hasCoreActionToday
                    ? 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-200 border-emerald-400/35'
                    : 'bg-amber-500/15 text-amber-900 dark:text-amber-200 border-amber-400/35'
                }`}
              >
                {hasCoreActionToday ? (
                  <ShieldCheck className="w-3.5 h-3.5" aria-hidden />
                ) : (
                  <ShieldAlert className="w-3.5 h-3.5" aria-hidden />
                )}
                {hasCoreActionToday ? 'Core action complete today' : 'Core action pending today'}
              </span>
              {streakDays > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-900 dark:text-orange-200 text-xs font-semibold border border-orange-400/35">
                  <Zap className="w-3.5 h-3.5" aria-hidden />
                  {streakDays}-day streak
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 dark:bg-neutral-800/90 text-neutral-800 dark:text-neutral-200 text-xs font-medium border border-neutral-200/80 dark:border-neutral-600">
                <CalendarClock className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 shrink-0" aria-hidden />
                {weekCheckIns} check-in{weekCheckIns === 1 ? '' : 's'} (7d)
              </span>
              <button
                type="button"
                onClick={() => {
                  analytics.track('settings_viewed', { source: 'home_performance_chip' });
                  navigate('/settings');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-500/10 text-sky-900 dark:text-sky-200 text-xs font-medium border border-sky-400/35 hover:bg-sky-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              >
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden />
                Performance: {performanceMode === 'minimal' ? 'Minimal' : 'Adaptive'}
              </button>
              {!hasCoreActionToday ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackCoreAction('core_status_indicator_quick_pulse');
                    navigate('/quick-check-in');
                  }}
                >
                  Complete quick pulse
                </Button>
              ) : null}
            </div>
          </div>
        </section>

        <HomeFlowSection
          title="1) Daily Reset"
          description="Start here to reset your day: check in, pulse, and anchor your baseline."
          isOpen={homeSections.coreFlow}
          onToggle={() => toggleHomeSection('coreFlow')}
        >
          {loginSnapshot ? (
            <Card className="p-4 border-emerald-200/80 bg-emerald-50/70 dark:border-emerald-900/40 dark:bg-emerald-950/20">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                    Login snapshot
                  </p>
                  <p className="mt-1 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    {loginSnapshot.headline}
                  </p>
                  <p className="mt-1 text-xs text-neutral-700 dark:text-neutral-300">{loginSnapshot.summary}</p>
                </div>
                <span className="rounded-full border border-emerald-300/70 bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:border-emerald-700 dark:bg-neutral-900 dark:text-emerald-300">
                  {loginSnapshot.riskLevel} risk
                </span>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-neutral-700 dark:text-neutral-300">
                {loginSnapshot.quickAdvice.slice(0, 3).map((tip) => (
                  <li key={tip} className="flex items-start gap-2">
                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-600" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <nav aria-label="Quick actions" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link
              to="/check-in"
              onClick={() => trackCoreAction('open_daily_checkin')}
              className="group min-h-28 rounded-2xl border border-violet-200/90 dark:border-violet-800/60 bg-white/90 dark:bg-neutral-800/90 p-4 hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-lg hover:shadow-violet-500/10 transition-all touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <Activity className="w-6 h-6 text-violet-600 mb-2 transition-transform" aria-hidden />
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Daily check-in</p>
              <p className="text-xs text-neutral-500 mt-0.5">Full score · ~3 min</p>
            </Link>
            <Link
              to="/quick-check-in"
              onClick={() => trackCoreAction('open_quick_pulse')}
              className="group min-h-28 rounded-2xl border border-fuchsia-200/90 dark:border-fuchsia-900/50 bg-white/90 dark:bg-neutral-800/90 p-4 hover:border-fuchsia-400 hover:shadow-lg hover:shadow-fuchsia-500/10 transition-all touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500"
            >
              <Zap className="w-6 h-6 text-fuchsia-600 mb-2 transition-transform" aria-hidden />
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Quick pulse</p>
              <p className="text-xs text-neutral-500 mt-0.5">Fast mood capture</p>
            </Link>
            <Link
              to="/timeline"
              onClick={() => trackCoreAction('open_timeline')}
              className="group min-h-28 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white/90 dark:bg-neutral-800/90 p-4 hover:border-violet-300 dark:hover:border-violet-600 hover:shadow-md transition-all touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <LineChart className="w-6 h-6 text-violet-600 mb-2 transition-transform" aria-hidden />
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Timeline</p>
              <p className="text-xs text-neutral-500 mt-0.5">See the arc</p>
            </Link>
            <Link
              to="/packs"
              className="group min-h-28 rounded-2xl border border-amber-200/90 dark:border-amber-900/40 bg-gradient-to-br from-amber-50/90 to-orange-50/50 dark:from-amber-950/40 dark:to-neutral-800/90 p-4 hover:border-amber-400 hover:shadow-md transition-all touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              <Package className="w-6 h-6 text-amber-700 dark:text-amber-400 mb-2 transition-transform" aria-hidden />
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Packs</p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">Guided playbooks</p>
            </Link>
          </nav>
        </HomeFlowSection>

        <HomeFlowSection
          title="2) Community Boost (Optional)"
          description="Use this after core actions to get peer support without breaking your flow."
          isOpen={homeSections.community}
          onToggle={() => toggleHomeSection('community')}
        >
        {socialRollout.allowed ? (
          <Card className="p-4 border-neutral-200/90 bg-white/90 dark:border-neutral-700 dark:bg-neutral-900/70">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">
                  Community assist (lightweight)
                </p>
                <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
                  {communityAssistVariant === 'minimal'
                    ? 'Optional support nudges with one clean action.'
                    : 'Optional support nudges without pulling you out of core flow.'}
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                  <span className="rounded-full border border-neutral-200 px-2 py-0.5 dark:border-neutral-700">
                    Variant: {communityAssistVariant}
                  </span>
                  <span className="rounded-full border border-neutral-200 px-2 py-0.5 dark:border-neutral-700">
                    Support-after-core: {weeklySupportRecap.supportRate}%
                  </span>
                  <span className="rounded-full border border-neutral-200 px-2 py-0.5 dark:border-neutral-700">
                    Suggested circle: {supportCircles[0] || 'Support'}
                  </span>
                </div>
                {communityAssistVariant === 'richer' && filteredSocialFeed[0]?.content ? (
                  <p className="mt-2 text-xs text-neutral-700 dark:text-neutral-300">
                    Peer insight: &quot;
                    {String(filteredSocialFeed[0].content).slice(0, 110)}
                    {String(filteredSocialFeed[0].content).length > 110 ? "..." : ""}
                    &quot;
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (socialGateLocked) {
                      redirectToSocialUnlock('assist_open_social');
                      return;
                    }
                    if (communityAssistVariant === 'minimal') {
                      setShowHomeSocialPanel(true);
                      trackSocialAction('compact_open_social', hasCoreActionToday, {
                        variant: communityAssistVariant,
                      });
                      recordHomeSocialConversionEvent({
                        kind: 'assist_action',
                        variant: communityAssistVariant,
                      });
                      recordHomeSocialConversionEvent({
                        kind: 'assist_follow_through',
                        variant: communityAssistVariant,
                      });
                      return;
                    }
                    setShowHomeSocialPanel(true);
                    setSocialPostDraft(socialSupportPrompt);
                    trackSocialAction('compact_prime_support_post', hasCoreActionToday, {
                      variant: communityAssistVariant,
                    });
                    recordHomeSocialConversionEvent({
                      kind: 'assist_action',
                      variant: communityAssistVariant,
                    });
                    recordHomeSocialConversionEvent({
                      kind: 'assist_follow_through',
                      variant: communityAssistVariant,
                    });
                  }}
                >
                  {communityAssistVariant === 'minimal' ? 'Open social support' : 'Prime support post'}
                </Button>
                {communityAssistVariant === 'richer' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (socialGateLocked) {
                        redirectToSocialUnlock('assist_search_circle');
                        return;
                      }
                      setShowHomeSocialPanel(true);
                      void applySupportCircle(supportCircles[0] || 'Support');
                      trackSocialAction('compact_search_support_circle', hasCoreActionToday, {
                        variant: communityAssistVariant,
                      });
                      recordHomeSocialConversionEvent({
                        kind: 'assist_action',
                        variant: communityAssistVariant,
                      });
                      recordHomeSocialConversionEvent({
                        kind: 'assist_follow_through',
                        variant: communityAssistVariant,
                      });
                    }}
                  >
                    Search support circle
                  </Button>
                ) : null}
                {socialGateLocked ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      trackCoreAction('compact_unlock_social_via_quick_pulse');
                      navigate('/quick-check-in');
                    }}
                  >
                    Unlock social
                  </Button>
                ) : null}
              </div>
            </div>
          </Card>
        ) : null}

        {socialRollout.allowed ? (
          <Card
            className={`p-5 border-neutral-200/90 bg-white/90 dark:border-neutral-700 dark:bg-neutral-900/70 ${
              canUseEnhancedMotion ? 'motion-safe:transition-[transform,box-shadow] motion-safe:duration-300' : ''
            } ${
              showHomeSocialPanel
                ? canUseEnhancedMotion
                  ? 'shadow-lg shadow-violet-500/10 motion-safe:-translate-y-0.5'
                  : 'shadow-md shadow-violet-500/5'
                : ''
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">
                  Integrated support layer
                </p>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {socialPanelTitle}
                </h2>
                <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-1">
                  {socialPanelSubtitle}
                </p>
                <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                  Today: {filteredSocialFeed.length} feed item{filteredSocialFeed.length === 1 ? '' : 's'} · {socialFriendResults.length} search result{socialFriendResults.length === 1 ? '' : 's'} · {socialSuggestions.length} suggested peer{socialSuggestions.length === 1 ? '' : 's'}
                  {socialFeed.length > filteredSocialFeed.length
                    ? ` · ${socialFeed.length - filteredSocialFeed.length} hidden by quality filter`
                    : ''}
                </p>
                {socialSuggestedFocus ? (
                  <p className="mt-1 text-[11px] text-violet-700 dark:text-violet-300">
                    Suggested focus from your snapshot: {socialSuggestedFocus}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setShowHomeSocialPanel((prev) => {
                      if (socialGateLocked) {
                        redirectToSocialUnlock('social_panel_toggle');
                        return prev;
                      }
                      const next = !prev;
                      if (next) {
                        recordHomeCoexistenceEvent('expand');
                      }
                      analytics.track('home_social_panel_toggled', {
                        expanded: next,
                        locked: socialGateLocked,
                      });
                      return next;
                    })
                  }
                >
                  {showHomeSocialPanel ? 'Hide social tools' : 'Show social tools'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (socialGateLocked) {
                      redirectToSocialUnlock('open_community_feed');
                      return;
                    }
                    trackSocialAction('open_full_social', hasCoreActionToday);
                    navigate('/community');
                  }}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Open community feed
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackCoreAction('return_to_core_from_social_header');
                    navigate('/check-in');
                  }}
                >
                  Back to check-in
                </Button>
              </div>
            </div>

            {dailySupportMission ? (
              <div className="mt-3 rounded-xl border border-emerald-300/70 bg-emerald-50/70 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  {dailySupportMission.title}
                </p>
                <p className="mt-1 text-xs text-neutral-700 dark:text-neutral-300">{dailySupportMission.description}</p>
                <div className="mt-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      dailySupportMission.run();
                    }}
                  >
                    {dailySupportMission.cta}
                  </Button>
                </div>
              </div>
            ) : null}

            {showSocialUnlockCelebration ? (
              <div
                className={`mt-3 rounded-xl border border-violet-300/80 bg-gradient-to-r from-violet-100/80 via-fuchsia-100/70 to-emerald-100/70 p-3 dark:border-violet-700/60 dark:from-violet-950/40 dark:via-fuchsia-950/30 dark:to-emerald-950/30 ${
                  canUseEnhancedMotion ? 'motion-safe:animate-pulse' : ''
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200">
                  Core unlocked social support
                </p>
                <p className="mt-1 text-xs text-neutral-700 dark:text-neutral-200">
                  Nice work. You completed today&apos;s core action — social support is now open to reinforce momentum.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSocialUnlockCelebration(false);
                      setShowHomeSocialPanel(true);
                      trackSocialAction('unlock_celebration_expand', true);
                    }}
                  >
                    Use support now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSocialUnlockCelebration(false);
                      trackCoreAction('unlock_celebration_back_to_core');
                      navigate('/timeline');
                    }}
                  >
                    Stay in core flow
                  </Button>
                </div>
              </div>
            ) : null}

            {socialGateLocked ? (
              <div className="mt-3 rounded-xl border border-amber-300/80 bg-amber-50/80 p-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
                <p className="font-semibold">Complete one core action to unlock social tools.</p>
                <p className="mt-1">
                  Run a quick pulse or full check-in first. This keeps TENSION focused on your daily relationship health before community browsing.
                </p>
                <div className="mt-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      trackCoreAction('unlock_social_via_quick_pulse');
                      navigate('/quick-check-in');
                    }}
                  >
                    Complete quick pulse
                  </Button>
                </div>
              </div>
            ) : showHomeSocialPanel ? (
              <>
                {action ? (
                  <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50/70 p-3 dark:border-sky-900/50 dark:bg-sky-950/25">
                    <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                      Intent-aware handoff
                    </p>
                    <p className="mt-1 text-xs text-neutral-700 dark:text-neutral-300">
                      You completed core guidance. If helpful, ask peers for practical support on today&apos;s focus.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSocialPostDraft(socialSupportPrompt);
                          analytics.track('home_social_handoff_prompt_clicked', {
                            rtiState: rti?.state || 'unknown',
                          });
                        }}
                      >
                        Use suggested support prompt
                      </Button>
                      <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
                        Suggested: &quot;{socialSupportPrompt}&quot;
                      </span>
                    </div>
                  </div>
                ) : null}
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="space-y-3">
                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 shadow-sm transition-[transform,box-shadow] duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md dark:border-neutral-700 dark:bg-neutral-900/80">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-1.5">
                        <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">Momentum loop</p>
                        <span className="rounded-full border border-neutral-300 px-2 py-0.5 text-[10px] font-medium text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">
                          {socialMomentum.completed}/3 complete
                        </span>
                      </div>
                      <div
                        className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700"
                        role="progressbar"
                        aria-label="Social momentum progress"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={socialMomentumProgressPct}
                      >
                        <div
                          className="h-full rounded-full bg-emerald-500 transition-[width] duration-300"
                          style={{ width: `${socialMomentumProgressPct}%` }}
                        />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <span
                          className={`inline-flex min-h-7 items-center rounded-full border px-2.5 py-1 text-[11px] ${
                            socialMomentum.goals.follows
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                              : 'border-neutral-300 text-neutral-600 dark:border-neutral-700 dark:text-neutral-300'
                          }`}
                        >
                          Follow 1
                        </span>
                        <span
                          className={`inline-flex min-h-7 items-center rounded-full border px-2.5 py-1 text-[11px] ${
                            socialMomentum.goals.comments
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                              : 'border-neutral-300 text-neutral-600 dark:border-neutral-700 dark:text-neutral-300'
                          }`}
                        >
                          Comment 1
                        </span>
                        <span
                          className={`inline-flex min-h-7 items-center rounded-full border px-2.5 py-1 text-[11px] ${
                            socialMomentum.goals.posts
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                              : 'border-neutral-300 text-neutral-600 dark:border-neutral-700 dark:text-neutral-300'
                          }`}
                        >
                          Post 1
                        </span>
                      </div>
                      <div className="mt-2 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[11px] text-neutral-500 dark:text-neutral-400" aria-live="polite">
                          {socialMomentum.done
                            ? 'Momentum loop complete. Keep your support streak alive.'
                            : `Next step: ${socialMomentumNextLabel}.`}
                        </p>
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              void runNextBestSocialAction();
                            }}
                            disabled={socialGateLocked || socialMomentum.done || socialActionBusy}
                            className="min-h-9 w-full px-3 sm:w-auto"
                            aria-label={
                              socialMomentum.done
                                ? 'Social momentum loop complete'
                                : `Do next best action: ${socialMomentumNextLabel}`
                            }
                          >
                            {socialActionBusy ? 'Working...' : 'Do next best action'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={runSocialMomentumNudge}
                            disabled={socialGateLocked || socialMomentum.done || socialActionBusy}
                            className="min-h-9 w-full px-3 sm:w-auto"
                            aria-label={
                              socialMomentum.done
                                ? 'Social momentum loop complete'
                                : `Guide me through next step: ${socialMomentumNextLabel}`
                            }
                          >
                            <ArrowRight className="mr-1 h-3.5 w-3.5" />
                            {socialMomentumGuideLabel}
                          </Button>
                        </div>
                      </div>
                      {socialActionRecap ? (
                        <div className="mt-2 rounded-lg border border-violet-200/80 bg-violet-50/70 p-2 dark:border-violet-900/50 dark:bg-violet-950/20">
                          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                            <p className="text-[11px] text-violet-800 dark:text-violet-200" aria-live="polite">
                              {socialActionRecap.message}
                              {socialActionRecap.nextLabel ? ` Next: ${socialActionRecap.nextLabel}.` : ''}
                            </p>
                            {socialActionRecap.nextLabel ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={socialActionBusy || socialGateLocked || socialMomentum.done}
                                onClick={() => {
                                  void runNextBestSocialAction();
                                }}
                                className="min-h-8 w-full px-2 text-[11px] sm:w-auto"
                              >
                                Continue
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {socialMomentum.done && !socialGateLocked ? (
                        <div className="mt-2 rounded-lg border border-emerald-200/80 bg-emerald-50/70 p-2 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                            <p className="text-[11px] text-emerald-800 dark:text-emerald-200">
                              3/3 complete. Nice work keeping support momentum active today.
                            </p>
                            <div className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="min-h-8 w-full px-2 text-[11px] sm:w-auto"
                                onClick={() => {
                                  analytics.track('home_social_momentum_complete_next_step', {
                                    destination: 'timeline',
                                  });
                                  navigate('/timeline');
                                }}
                              >
                                Review timeline
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="min-h-8 w-full px-2 text-[11px] sm:w-auto"
                                onClick={() => {
                                  analytics.track('home_social_momentum_complete_next_step', {
                                    destination: 'community',
                                  });
                                  navigate('/community');
                                }}
                              >
                                Keep supporting
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {socialGateLocked ? (
                        <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                          Complete one core action to unlock momentum guidance.
                        </p>
                      ) : null}
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                      <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
                        Targeted support request
                      </p>
                      <p className="mb-2 text-[11px] text-neutral-500 dark:text-neutral-400">
                        Ask for one specific idea you can apply to {socialRelationshipLabel} today.
                      </p>
                      <textarea
                        value={socialPostDraft}
                        onChange={(e) => setSocialPostDraft(e.target.value)}
                        rows={3}
                        placeholder={socialSupportPrompt}
                        className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm leading-relaxed dark:border-neutral-700 dark:bg-neutral-800"
                      />
                      <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                        Best results: share one situation, what you tried, and the exact kind of feedback you want.
                      </p>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
                          {socialPostDraft.trim().length} chars
                        </span>
                        <Button
                          size="sm"
                          onClick={handleHomeSocialPost}
                          disabled={socialBusyPost}
                          className="min-h-9 w-full sm:w-auto"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {socialBusyPost ? 'Posting...' : 'Post'}
                        </Button>
                      </div>
                      <p className="mt-2 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
                        Quick starters
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {socialIcebreakers.map((prompt) => (
                          <button
                            key={prompt}
                            type="button"
                            onClick={() => setSocialPostDraft(prompt)}
                            className="rounded-full border border-violet-200 px-2.5 py-1 text-[11px] text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-900/20"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                      <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
                        Peer match search
                      </p>
                      <p className="mb-2 text-[11px] text-neutral-500 dark:text-neutral-400">
                        Match with people who have solved a similar communication challenge.
                      </p>
                      <div className="flex gap-2">
                        <input
                          value={socialFriendQuery}
                          onChange={(e) => setSocialFriendQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleFriendSearch();
                            }
                          }}
                          placeholder="Search by name, challenge, or communication focus"
                          className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-800"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleFriendSearch}
                          disabled={socialSearching}
                          className="min-h-9 min-w-9 px-3"
                          aria-label="Search social profiles"
                        >
                          <Search className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Search</span>
                        </Button>
                      </div>
                      <div className="mt-2 space-y-2 max-h-52 overflow-y-auto">
                        {socialSearching ? (
                          <div className="space-y-2">
                            {[1, 2, 3].map((i) => (
                              <div key={i} className="rounded-lg border border-neutral-200 px-2 py-1.5 dark:border-neutral-700">
                                <div className="h-3 w-32 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
                                <div className="mt-1 h-2.5 w-48 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800" />
                              </div>
                            ))}
                          </div>
                        ) : socialFriendResults.length === 0 ? (
                          <p className="text-xs text-neutral-500">Search for peers who have handled a similar situation.</p>
                        ) : (
                          socialFriendResults.map((profile, index) => (
                            <div key={profile.userId} className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 px-2 py-1.5 dark:border-neutral-700">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                  {profile.displayName || 'Community member'}
                                </p>
                                <p className="text-xs text-neutral-500 truncate">{profile.headline || 'Relationship community'}</p>
                                <p className="text-[11px] text-violet-600 dark:text-violet-300 truncate">
                                  Connection fit {getConnectionFitScore(profile)}%
                                  {socialMutualsByUser[profile.userId]?.mutualCount
                                    ? ` · ${socialMutualsByUser[profile.userId].mutualCount} mutual connection${socialMutualsByUser[profile.userId].mutualCount === 1 ? '' : 's'}`
                                    : ''}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleFriendFollow(profile.userId, 'search', index + 1)}
                                disabled={Boolean(pendingFollowIds[profile.userId])}
                                className="min-h-9 w-full px-3 sm:w-auto"
                                aria-label={`Follow ${profile.displayName || 'community member'} from search`}
                              >
                                {pendingFollowIds[profile.userId] ? 'Following...' : 'Follow'}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                      <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-2">Support circles</p>
                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-2">
                        Fast paths to peers discussing your current relationship focus.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {supportCircles.map((circle) => (
                          <button
                            key={circle}
                            type="button"
                            onClick={() => {
                              void applySupportCircle(circle);
                            }}
                            className="inline-flex min-h-8 items-center rounded-full border border-sky-200 px-2.5 py-1 text-[11px] text-sky-700 transition-colors hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-sky-800 dark:text-sky-300 dark:hover:bg-sky-900/20"
                          >
                            {circle}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                      <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-2">Feed quality controls</p>
                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-2">
                        Reduce noise and prioritize practical support posts.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setSocialQualityMode('balanced');
                            trackSocialAction('quality_mode_balanced', hasCoreActionToday);
                            analytics.track('home_social_quality_mode_changed', {
                              mode: 'balanced',
                              afterCore: hasCoreActionToday,
                            });
                            recordHomeSocialConversionEvent({ kind: 'quality_balanced' });
                          }}
                          className={`rounded-full border px-2.5 py-1 text-[11px] ${
                            socialQualityMode === 'balanced'
                              ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/20 dark:text-violet-300'
                              : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                          }`}
                        >
                          Balanced
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSocialQualityMode('strict');
                            trackSocialAction('quality_mode_strict', hasCoreActionToday);
                            analytics.track('home_social_quality_mode_changed', {
                              mode: 'strict',
                              afterCore: hasCoreActionToday,
                            });
                            recordHomeSocialConversionEvent({ kind: 'quality_strict' });
                          }}
                          className={`rounded-full border px-2.5 py-1 text-[11px] ${
                            socialQualityMode === 'strict'
                              ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/20 dark:text-violet-300'
                              : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                          }`}
                        >
                          Strict
                        </button>
                      </div>
                      <p className="mt-2 text-[11px] text-neutral-500 dark:text-neutral-400">
                        Minimum post length threshold: {socialTuning.feedMinPostChars} characters.
                      </p>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                      <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-2 inline-flex items-center gap-1">
                        <Compass className="w-3.5 h-3.5" /> Peers to learn with today
                      </p>
                      <div className="mb-2 flex flex-wrap gap-1.5 text-[11px]">
                        <span className="rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">
                          Source: {socialSuggestionContext.source === 'intelligence' ? 'Intelligence engine' : 'Fallback'}
                        </span>
                        {socialSuggestionContext.policyVersion ? (
                          <span className="rounded-full border border-violet-300 px-2 py-0.5 text-violet-700 dark:border-violet-700 dark:text-violet-300">
                            Policy v{socialSuggestionContext.policyVersion}
                          </span>
                        ) : null}
                      </div>
                      <div className="space-y-2">
                        {socialSuggestions.length === 0 ? (
                          <p className="text-xs text-neutral-500">Discovery suggestions will appear as your social graph grows.</p>
                        ) : (
                          socialSuggestions.map((profile, index) => (
                            <div
                              key={profile.userId}
                              className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 px-2 py-1.5 dark:border-neutral-700"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                  {profile.displayName || 'Community member'}
                                </p>
                                <p className="truncate text-xs text-neutral-500">
                                  {profile.commonInterests
                                    ? `${profile.commonInterests} shared interests`
                                    : profile.headline || 'Relationship community'}
                                </p>
                                <p className="truncate text-[11px] text-violet-600 dark:text-violet-300">
                                  Connection fit {getConnectionFitScore(profile)}%
                                  {socialMutualsByUser[profile.userId]?.mutualCount
                                    ? ` · ${socialMutualsByUser[profile.userId].mutualCount} mutual connection${socialMutualsByUser[profile.userId].mutualCount === 1 ? '' : 's'}`
                                    : ''}
                                </p>
                                {socialSuggestionContext.source === 'intelligence' && profile?.explain ? (
                                  <p className="truncate text-[10px] text-neutral-500 dark:text-neutral-400">
                                    Why shown: mutual {Number(profile?.explain?.mutualCount || profile?.mutualCount || 0)} ·
                                    interests {Number(profile?.explain?.commonInterests || profile?.commonInterests || 0)} ·
                                    quality {Number(profile?.explain?.qualityScore || profile?.qualityScore || 0)}
                                  </p>
                                ) : null}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleFriendFollow(profile.userId, 'suggestion', index + 1)}
                                disabled={Boolean(pendingFollowIds[profile.userId])}
                                className="min-h-9 px-3"
                                aria-label={`Follow ${profile.displayName || 'community member'} from suggestions`}
                              >
                                <UserPlus className="w-3.5 h-3.5 mr-1" />
                                {pendingFollowIds[profile.userId] ? 'Following...' : 'Follow'}
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                      <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-2">Stay core-first</p>
                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-2">
                        Keep progress anchored to RTI: check in, run your action card, then use social for reinforcement.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            trackCoreAction('social_panel_return_to_checkin');
                            navigate('/check-in');
                          }}
                        >
                          Open check-in
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            trackCoreAction('social_panel_open_timeline');
                            navigate('/timeline');
                          }}
                        >
                          Open timeline
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-neutral-200 bg-white/90 p-3 dark:border-neutral-700 dark:bg-neutral-900/80">
                    <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-2">Community highlights</p>
                    <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
                      {socialLoading ? (
                        <div className="space-y-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="rounded-lg border border-neutral-200 p-2.5 dark:border-neutral-700">
                              <div className="flex items-center justify-between gap-2">
                                <div className="h-3 w-24 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
                                <div className="h-2.5 w-16 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800" />
                              </div>
                              <div className="mt-2 h-2.5 w-full animate-pulse rounded bg-neutral-100 dark:bg-neutral-800" />
                              <div className="mt-1 h-2.5 w-4/5 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800" />
                            </div>
                          ))}
                        </div>
                      ) : filteredSocialFeed.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-violet-300 p-3 text-xs text-neutral-600 dark:border-violet-800 dark:text-neutral-300">
                          <p className="font-medium text-violet-700 dark:text-violet-300">No high-signal posts yet — support starts with one practical signal.</p>
                          <p className="mt-1">
                            Quick win: post one reflection with context, follow two people, and comment once to build your support loop.
                          </p>
                        </div>
                      ) : (
                        filteredSocialFeed.map((post: any) => (
                          <div
                            key={post.id}
                            className={`rounded-lg border border-neutral-200 p-2.5 dark:border-neutral-700 ${
                              optimisticPostIds[String(post?.id || '')] ? 'opacity-80' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{post.authorName || 'Member'}</p>
                              <p className="text-[11px] text-neutral-500">
                                {optimisticPostIds[String(post?.id || '')] ? 'Sending...' : formatSocialTime(post.createdAt)}
                              </p>
                            </div>
                            <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{post.content}</p>
                            <div className="mt-2 flex items-center gap-1.5">
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleHomeSocialReact(post.id, 'like')}>
                                <Heart className="w-3.5 h-3.5 mr-1" /> {Number(post?.reactions?.like || 0)}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleHomeSocialReact(post.id, 'insight')}>
                                <Sparkles className="w-3.5 h-3.5 mr-1" /> {Number(post?.reactions?.insight || 0)}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleHomeSocialReact(post.id, 'support')}>
                                <MessageCircle className="w-3.5 h-3.5 mr-1" /> {Number(post?.reactions?.support || 0)}
                              </Button>
                            </div>
                            <div className="mt-2 flex gap-2">
                              <input
                                value={socialCommentDrafts[post.id] || ''}
                                onChange={(e) => setSocialCommentDrafts((prev) => ({ ...prev, [post.id]: e.target.value }))}
                                placeholder="Comment..."
                                disabled={Boolean(pendingCommentIds[post.id])}
                                className="w-full rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-800"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={Boolean(pendingCommentIds[post.id])}
                                onClick={() => handleHomeSocialComment(post.id)}
                              >
                                {pendingCommentIds[post.id] ? 'Sending...' : 'Send'}
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                {isSocialFirstRun ? (
                  <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                      First-time social onboarding
                    </p>
                    <ol className="mt-2 grid gap-2 text-xs text-neutral-700 dark:text-neutral-300 sm:grid-cols-3">
                      <li className="rounded-lg border border-emerald-200 bg-white/80 px-2 py-1.5 dark:border-emerald-900/50 dark:bg-neutral-900/60">
                        1) Follow at least 2 people using search or suggestions.
                      </li>
                      <li className="rounded-lg border border-emerald-200 bg-white/80 px-2 py-1.5 dark:border-emerald-900/50 dark:bg-neutral-900/60">
                        2) Publish one short relationship insight.
                      </li>
                      <li className="rounded-lg border border-emerald-200 bg-white/80 px-2 py-1.5 dark:border-emerald-900/50 dark:bg-neutral-900/60">
                        3) Leave one supportive comment to build reciprocity.
                      </li>
                    </ol>
                  </div>
                ) : null}
                <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/70 p-3 dark:border-indigo-900/50 dark:bg-indigo-950/20">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                    Weekly wins recap
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-neutral-700 dark:text-neutral-300 sm:grid-cols-4">
                    <div className="rounded-lg border border-indigo-200/70 bg-white/80 px-2 py-1.5 dark:border-indigo-900/50 dark:bg-neutral-900/60">
                      Core actions: {weeklySupportRecap.coreActions}
                    </div>
                    <div className="rounded-lg border border-indigo-200/70 bg-white/80 px-2 py-1.5 dark:border-indigo-900/50 dark:bg-neutral-900/60">
                      Social actions: {weeklySupportRecap.socialActions}
                    </div>
                    <div className="rounded-lg border border-indigo-200/70 bg-white/80 px-2 py-1.5 dark:border-indigo-900/50 dark:bg-neutral-900/60">
                      Support-after-core: {weeklySupportRecap.supportRate}%
                    </div>
                    <div className="rounded-lg border border-indigo-200/70 bg-white/80 px-2 py-1.5 dark:border-indigo-900/50 dark:bg-neutral-900/60">
                      Panel opens: {weeklySupportRecap.panelExpands}
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-neutral-600 dark:text-neutral-400">
                    Focus for next week: keep social actions anchored to core progress for stronger retention.
                  </p>
                </div>
              </>
            ) : (
              <div className="mt-3 rounded-xl border border-dashed border-neutral-300/80 bg-neutral-50/80 p-3 text-xs text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800/50 dark:text-neutral-300">
                Social tools are hidden to keep focus on core relationship health. Expand when you want community support.
              </div>
            )}
          </Card>
        ) : null}
        </HomeFlowSection>

        <HomeFlowSection
          sectionId="relationship-intelligence"
          title="3) Relationship Intelligence"
          description="Understand your pattern, risk, and timing windows before choosing your next move."
          isOpen={homeSections.insights}
          onToggle={() => toggleHomeSection('insights')}
        >
        <Card className="p-4 border-violet-200/80 bg-gradient-to-br from-violet-50/90 via-indigo-50/80 to-white dark:border-violet-900/50 dark:from-violet-950/30 dark:via-indigo-950/20 dark:to-neutral-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                Interactive move simulator
              </p>
              <p className="mt-1 text-sm text-neutral-800 dark:text-neutral-100">
                Pick a moment style and reveal a live next-move script.
              </p>
            </div>
            <span className="rounded-full border border-violet-200/80 bg-white/80 px-2 py-1 text-[11px] font-medium text-violet-700 dark:border-violet-800 dark:bg-neutral-900/70 dark:text-violet-200">
              {riMicroMomentumLabel}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {RI_MICRO_SCENARIOS.map((scenario) => (
              <Button
                key={scenario.id}
                type="button"
                size="sm"
                variant={riMicroScenario === scenario.id ? 'default' : 'outline'}
                className="h-8 px-3 text-xs"
                onClick={() => {
                  setRiMicroScenario(scenario.id);
                  setRiMicroRevealStep(1);
                  trackRiEvent('ri_interaction_started', { module: 'micro_simulator', scenario: scenario.id });
                }}
              >
                {scenario.label}
              </Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-300">{riMicroScenarioConfig.cue}</p>
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Message intensity
              </label>
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400">{riMicroEnergy}%</span>
            </div>
            <input
              type="range"
              min={25}
              max={100}
              step={1}
              value={riMicroEnergy}
              onChange={(event) => setRiMicroEnergy(Number(event.target.value))}
              className="w-full accent-violet-600"
            />
          </div>
          <div className="mt-3 rounded-lg border border-violet-200/70 bg-white/80 p-3 dark:border-violet-900/50 dark:bg-neutral-900/70">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
              Suggested next move
            </p>
            <p className="mt-1 text-sm text-neutral-800 dark:text-neutral-100">
              {riMicroVisibleMove}
              {!riMicroDone ? ' ...' : ''}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-3 text-xs"
                onClick={() => {
                  setRiMicroRevealStep((value) => Math.min(value + 1, 4));
                  trackRiEvent('ri_interaction_started', { module: 'micro_simulator', action: 'reveal_more' });
                }}
                disabled={riMicroDone}
              >
                {riMicroDone ? 'Fully revealed' : 'Reveal more'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-3 text-xs"
                onClick={() => {
                  setRiMicroRevealStep(1);
                  setRiMicroEnergy(62);
                  trackRiEvent('ri_interaction_completed', { module: 'micro_simulator', action: 'reset' });
                }}
              >
                Reset drill
              </Button>
            </div>
          </div>
        </Card>

        {relationshipIntel && adaptive?.profile && riInteractiveEnabled ? (
          <IntelligenceBriefing
            profile={adaptive.profile}
            onJumpToSection={jumpToPriorityRiTool}
            syncHint={`Mission progress ${riMissionState.completedStepIds.length}/${relationshipIntel.repairPlan.steps.length} · Variant ${riVariant}`}
          />
        ) : null}

        {relationshipIntel?.model && riInteractiveEnabled ? (
          <TrendTimingRailCard
            model={relationshipIntel.model}
            onTrack={(event, properties) => {
              trackRiEvent('ri_interaction_started', { module: 'trend_timing', event, ...(properties || {}) });
            }}
          />
        ) : null}

        {relationshipIntel && riInteractiveEnabled ? (
          <MessagePathComposerCard
            selectedStyle={counterfactualStyle}
            onStyleChange={(style) => setCounterfactualStyle(style)}
            projection={counterfactual}
            script={counterfactualScript}
            onUsePath={() => {
              relationshipIntel.selectCounterfactualPath(counterfactualStyle);
              toast.success('Path saved. We will auto-calibrate this prediction after your next check-in.');
            }}
            onTrack={(event, properties) => {
              trackRiEvent('ri_interaction_started', { module: 'message_path', event, ...(properties || {}) });
            }}
          />
        ) : null}

        {relationshipIntel && riInteractiveEnabled ? (
          <Card className="p-4 border-indigo-200/80 bg-indigo-50/70 dark:border-indigo-900/50 dark:bg-indigo-950/25">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                  Intelligence to action
                </p>
                <p className="mt-1 text-sm text-neutral-800 dark:text-neutral-100">
                  Next best move: <span className="font-semibold">{relationshipIntel.twin.nextBestMove}</span>
                </p>
                <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
                  Use your intelligence output as an execution plan in Tools and Practice.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackRiEvent('ri_cta_clicked', { target: 'tools_section' });
                    setHomeSections((prev) => ({
                      ...prev,
                      tools: true,
                    }));
                  }}
                >
                  Open tools section
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    adaptive?.recordActivity('home_ri_cta', {});
                    trackRiEvent('ri_cta_clicked', { target: 'full_intelligence_view' });
                    setHomeSections((prev) => ({
                      ...prev,
                      tools: true,
                    }));
                  }}
                >
                  Open tools and practice
                </Button>
              </div>
            </div>
          </Card>
        ) : null}

        {relationshipIntel && riInteractiveEnabled ? (
          <RepairMissionTimelineCard
            plan={relationshipIntel.repairPlan}
            mission={riMissionState}
            onToggleStep={toggleRiMissionStep}
            onReset={resetRiMission}
            onTrack={(event, properties) => {
              trackRiEvent('ri_interaction_started', { module: 'repair_mission', event, ...(properties || {}) });
            }}
          />
        ) : null}

        {relationshipIntel && riInteractiveEnabled ? (
          <StabilityCockpitCard
            osScore={relationshipIntel.osScore}
            confidenceBand={relationshipIntel.confidenceBand}
            quickWins={relationshipIntel.quickWins}
            adaptiveChallenges={riAdaptiveEnabled ? relationshipIntel.adaptiveChallenges : relationshipIntel.adaptiveChallenges.slice(0, 1)}
            isPremium={isPremium}
            onUpgrade={() => {
              trackRiEvent('ri_upgrade_prompt_clicked', { target: 'pricing_from_ri' });
              navigate('/pricing');
            }}
            onTrack={(event, properties) => {
              trackRiEvent('ri_interaction_started', { module: 'stability_cockpit', event, ...(properties || {}) });
            }}
          />
        ) : null}

        {relationshipIntel && !riInteractiveEnabled && !subscriptionPlanLoading ? (
          <Card className="p-5 border-indigo-200/80 bg-gradient-to-br from-indigo-50/90 via-violet-50/80 to-white dark:from-indigo-950/40 dark:via-violet-950/30 dark:to-neutral-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
              Relationship Intelligence
            </p>
            <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-300">
              {isCouplesMode
                ? relationshipIntel.model.summary
                : 'Switch to Couples mode to unlock Relationship Intelligence on Home.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {isCouplesMode ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    trackRiEvent('ri_cta_clicked', { target: 'open_tools_from_ri_control' });
                    setHomeSections((prev) => ({
                      ...prev,
                      tools: true,
                    }));
                  }}
                >
                  Open tools and practice
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    analytics.track('ri_cta_clicked', { target: 'switch_to_couples_mode_from_ri', variant: riVariant, premium: isPremium });
                    navigate('/settings');
                  }}
                >
                  Switch to couples mode
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => {
                  trackRiEvent('ri_upgrade_prompt_clicked', { target: 'pricing_from_ri_control' });
                  navigate('/pricing');
                }}
              >
                Unlock advanced mode
              </Button>
            </div>
          </Card>
        ) : null}

        {riDebugVisible ? (
          <Card className="p-4 border-amber-200 bg-amber-50/80 dark:border-amber-900/50 dark:bg-amber-950/20">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
              RI experiment health
            </p>
            <p className="mt-1 text-xs text-amber-900 dark:text-amber-100">
              Variant: <span className="font-semibold">{riVariant}</span> · rollout eligible:{' '}
              <span className="font-semibold">{riRolloutEligible ? 'yes' : 'no'}</span> · rollout pct:{' '}
              <span className="font-semibold">{riExperimentConfig.rolloutPct}%</span> · forced:{' '}
              <span className="font-semibold">{riExperimentConfig.forcedVariant || 'none'}</span>
            </p>
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleResetRiExperimentStats}
                disabled={riResetBusy}
                className="border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-100 dark:hover:bg-amber-900/30"
              >
                {riResetBusy ? 'Resetting…' : 'Reset RI experiment stats'}
              </Button>
            </div>
            <div className="mt-2 rounded border border-amber-200 bg-white/70 p-2 dark:border-amber-900/60 dark:bg-neutral-900/40">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                Rollout presets
              </p>
              <p className="mt-1 text-[11px] text-amber-900 dark:text-amber-100">
                Recommendation: <span className="font-semibold uppercase">{riRolloutRecommendation.action}</span>
                {' '}to {riRolloutRecommendation.targetRolloutPct}% — {riRolloutRecommendation.reason}
              </p>
              <p className="mt-1 text-[11px] text-amber-900 dark:text-amber-100">
                Cooldown:{' '}
                <span className="font-semibold">
                  {riRolloutLocked
                    ? `${Math.ceil(riRolloutCooldownRemainingMs / 60000)} minute${Math.ceil(riRolloutCooldownRemainingMs / 60000) === 1 ? '' : 's'} remaining`
                    : 'ready'}
                </span>{' '}
                (rollback bypasses cooldown)
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {([10, 20, 35, 50] as const).map((pct) => (
                  <Button
                    key={pct}
                    size="sm"
                    variant={riExperimentConfig.enabled && riExperimentConfig.rolloutPct === pct ? 'default' : 'outline'}
                    className="h-7 px-2.5 text-[11px]"
                    onClick={() => applyRiRolloutPreset(pct)}
                    disabled={riRolloutLocked}
                  >
                    {pct}%
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant={!riExperimentConfig.enabled || riExperimentConfig.rolloutPct === 0 ? 'default' : 'outline'}
                  className="h-7 px-2.5 text-[11px] border-red-300 text-red-800 hover:bg-red-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/30"
                  onClick={() => applyRiRolloutPreset('rollback')}
                >
                  Rollback
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 px-2.5 text-[11px]"
                  onClick={applyRecommendedRiRollout}
                  disabled={
                    riRolloutRecommendation.action === 'hold' ||
                    (riRolloutLocked && riRolloutRecommendation.action !== 'rollback')
                  }
                >
                  Apply recommendation
                </Button>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-amber-900 dark:text-amber-100 sm:grid-cols-4">
              <div className="rounded border border-amber-200 bg-white/80 px-2 py-1 dark:border-amber-900/60 dark:bg-neutral-900/50">
                Impressions: {riExperimentHealth.totals.impressions}
              </div>
              <div className="rounded border border-amber-200 bg-white/80 px-2 py-1 dark:border-amber-900/60 dark:bg-neutral-900/50">
                Interactions: {riExperimentHealth.totals.interactions}
              </div>
              <div className="rounded border border-amber-200 bg-white/80 px-2 py-1 dark:border-amber-900/60 dark:bg-neutral-900/50">
                Completions: {riExperimentHealth.totals.completions}
              </div>
              <div className="rounded border border-amber-200 bg-white/80 px-2 py-1 dark:border-amber-900/60 dark:bg-neutral-900/50">
                Upgrades: {riExperimentHealth.totals.upgradeClicks}
              </div>
            </div>
            <div className="mt-2 grid gap-2 text-[11px] text-amber-900 dark:text-amber-100 sm:grid-cols-3">
              {(['control', 'interactive_v1', 'adaptive_v2'] as const).map((variantKey) => {
                const row = riExperimentHealth.variants[variantKey];
                return (
                  <div
                    key={variantKey}
                    className="rounded border border-amber-200 bg-white/80 px-2 py-1 dark:border-amber-900/60 dark:bg-neutral-900/50"
                  >
                    <p className="font-semibold">{variantKey}</p>
                    <p>Imp {row.impressions}</p>
                    <p>Int {row.interactionRatePct}% · Comp {row.completionRatePct}%</p>
                    <p>Upgrade {row.upgradeRatePct}%</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 grid gap-2 text-[11px] text-amber-900 dark:text-amber-100 sm:grid-cols-2">
              {riExperimentHealth.promotion.map((candidate) => (
                <div
                  key={candidate.variant}
                  className="rounded border border-amber-200 bg-white/80 px-2 py-1 dark:border-amber-900/60 dark:bg-neutral-900/50"
                >
                  <p className="font-semibold">{candidate.variant} vs control</p>
                  <p>
                    Lift {candidate.liftPct}% · confidence {candidate.confidence}%
                  </p>
                  <p>
                    Sample ready: {candidate.sampleReady ? 'yes' : 'no'} · promote ready:{' '}
                    {candidate.eligibleForPromotion ? 'yes' : 'no'}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <Card className="p-5 border-violet-200 bg-gradient-to-br from-violet-50 to-white">
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-neutral-900">Today in 10 seconds</p>
              <p className="text-xs text-neutral-600">Fast pulse for busy days. You can always do the full check-in later.</p>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[85, 65, 45, 25].map((score) => (
                <Button
                  key={score}
                  size="sm"
                  variant="outline"
                  onClick={() => saveTenSecondCheckIn(score, 'busy_day')}
                >
                  {score >= 80 ? 'Great' : score >= 60 ? 'Good' : score >= 40 ? 'Okay' : 'Hard'}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {['work_stress', 'low_energy', 'quality_time', 'conflict'].map((tag) => (
                <button
                  key={tag}
                  onClick={() => saveTenSecondCheckIn(60, tag)}
                  className="rounded-full border border-neutral-300 px-3 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
                >
                  {tag.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </Card>

        {activeRelationship && (
          <Card className="p-5 border-neutral-200 bg-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-900">Saved relationship profile</p>
                <p className="text-xs text-neutral-600">
                  {activeRelationship.name} · {activeRelationship.type || 'relationship'} · this stays remembered for future check-ins
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate('/relationships')}>
                Edit
              </Button>
            </div>
          </Card>
        )}

        {missedRecovery.show && (
          <Card className="p-5 border-amber-300 bg-amber-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">Missed-day recovery</p>
                <p className="text-xs text-amber-800">
                  It has been {missedRecovery.days} days. No guilt, just restart with a quick catch-up.
                </p>
              </div>
                <Button
                  size="sm"
                  onClick={() => {
                    trackCoreAction('missed_day_catchup');
                    navigate('/quick-check-in');
                  }}
                >
                Catch Up
              </Button>
            </div>
          </Card>
        )}

        {weeklyLoop && (
          <Card className="p-5 border-emerald-200 bg-emerald-50/60">
            <p className="text-sm font-semibold text-emerald-900">Weekly progress check</p>
            <p className="text-xs text-emerald-800 mt-1">
              Last 7 days average score: {weeklyLoop.recentAvg} vs the previous 7 days: {weeklyLoop.priorAvg}.{' '}
              {weeklyLoop.delta > 0 ? `Down ${weeklyLoop.delta} points (strong progress).` : weeklyLoop.delta < 0 ? `Up ${Math.abs(weeklyLoop.delta)} points (worth attention).` : 'Steady this week.'}
            </p>
          </Card>
        )}
        </HomeFlowSection>

        <HomeFlowSection
          sectionId="tools-and-practice"
          title="4) Tools and Practice"
          description="Act on your intelligence with guided steps, routines, and practical support tools."
          isOpen={homeSections.tools}
          onToggle={() => toggleHomeSection('tools')}
        >
        <Card className="p-5 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-violet-50 dark:border-fuchsia-900/40 dark:from-fuchsia-950/40 dark:to-violet-950/30">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-fuchsia-900 dark:text-fuchsia-100">Relationship tools hub</p>
              <p className="text-xs text-fuchsia-800 dark:text-fuchsia-200/90">
                {adaptive ? (
                  <>
                    <span className="font-medium text-fuchsia-950 dark:text-fuchsia-50">
                      {adaptive.profile.dataQuality.label} · signal {Math.round(adaptive.profile.signalStrength * 100)}%
                    </span>
                    <span className="mt-0.5 block">
                      Next step: {adaptive.profile.priorityTools[0]?.label ?? 'open your tools'} —{' '}
                      {adaptive.profile.priorityTools[0]?.reason ?? 'tools reorder to your focus.'}
                    </span>
                  </>
                ) : (
                  'Try focused tools like Tension Delta, Repair Replay, Micro-Coach, and more.'
                )}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                adaptive?.recordActivity('home_ri_cta', {});
                setHomeSections((prev) => ({
                  ...prev,
                  insights: true,
                }));
              }}
            >
              Open intelligence
            </Button>
          </div>
        </Card>

        {isPremium && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/insights"
              className="flex items-center justify-center gap-2 rounded-2xl border border-blue-200/80 dark:border-blue-900/50 bg-blue-50/80 dark:bg-blue-950/30 py-3 px-4 text-sm font-medium text-blue-900 dark:text-blue-100 hover:bg-blue-100/90 dark:hover:bg-blue-950/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <LineChart className="w-4 h-4 shrink-0" aria-hidden />
              Pattern insights — spot your patterns
            </Link>
            <Link
              to="/date-journal"
              className="flex items-center justify-center gap-2 rounded-2xl border border-indigo-200/80 dark:border-indigo-900/50 bg-indigo-50/80 dark:bg-indigo-950/25 py-3 px-4 text-sm font-medium text-indigo-900 dark:text-indigo-100 hover:bg-indigo-100/90 dark:hover:bg-indigo-950/45 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <BookOpen className="w-4 h-4 shrink-0" aria-hidden />
              Connection journal — save what happened
            </Link>
          </div>
        )}

        {/* Relationship Switcher - Show if multiple relationships */}
        {relationships.length > 1 && activeRelationship && (
          <nav aria-label="Relationship selection" className="relative" ref={relationshipMenuRef}>
            <button
              onClick={() => setShowRelationshipMenu(!showRelationshipMenu)}
              className="tension-glow w-full flex items-center justify-between p-4 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:border-neutral-300 dark:hover:border-neutral-500 transition-colors"
              aria-expanded={showRelationshipMenu}
              aria-haspopup="true"
              aria-label={`Current relationship: ${activeRelationship.name}. Click to switch relationships.`}
            >
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-pink-500" aria-hidden="true" />
                <div className="text-left">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{activeRelationship.name}</p>
                  <p className="text-xs text-neutral-500">
                    {activeRelationship.type.charAt(0).toUpperCase() + activeRelationship.type.slice(1)}
                  </p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${showRelationshipMenu ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {showRelationshipMenu && (
              <div 
                className="tension-glow absolute top-full left-0 right-0 mt-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-10"
                role="menu"
                aria-label="Select relationship"
              >
                <div className="p-2">
                  {relationships.map((rel: any) => (
                    <button
                      key={rel.id}
                      onClick={() => switchRelationship(rel.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        rel.id === activeRelationshipId
                          ? 'bg-violet-50 border border-violet-200'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'
                      }`}
                      role="menuitem"
                      aria-current={rel.id === activeRelationshipId ? 'true' : undefined}
                      aria-label={`Switch to ${rel.name} relationship`}
                    >
                      <Heart className={`w-4 h-4 ${rel.id === activeRelationshipId ? 'text-pink-500' : 'text-neutral-400'}`} aria-hidden="true" />
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{rel.name}</p>
                        <p className="text-xs text-neutral-500">
                          {rel.type.charAt(0).toUpperCase() + rel.type.slice(1)}
                        </p>
                      </div>
                      {rel.id === activeRelationshipId && (
                        <div className="w-2 h-2 rounded-full bg-violet-600" aria-label="Active" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="border-t border-neutral-200 dark:border-neutral-700 p-2">
                  <button
                    onClick={() => {
                      setShowRelationshipMenu(false);
                      navigate('/relationships');
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                  >
                    Manage Relationships
                  </button>
                </div>
              </div>
            )}
          </nav>
        )}

        {!rti ? (
          <Card className="overflow-hidden border-violet-200/80 dark:border-violet-800/50 bg-gradient-to-br from-white via-violet-50/40 to-fuchsia-50/50 dark:from-neutral-900 dark:via-violet-950/30 dark:to-neutral-900 p-8 text-center space-y-6 shadow-md shadow-violet-500/5">
            <div className="space-y-3">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-600/10 text-3xl" aria-hidden>
                ✨
              </div>
              <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Your score is waiting</h2>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm max-w-sm mx-auto leading-relaxed">
                One short check-in unlocks your score, forecast, streaks, and today&apos;s coaching card — built to feel
                like a game you play for a calmer week.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/check-in')} size="lg" className="bg-violet-600 hover:bg-violet-700 text-white">
                Start daily check-in
              </Button>
              <Button onClick={() => navigate('/quick-check-in')} size="lg" variant="outline" className="border-violet-300">
                Try quick pulse first
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {/* RTI Display */}
            <Card className="p-8 space-y-6 border-violet-200/60 dark:border-violet-900/40 bg-white/90 dark:bg-neutral-900/60 shadow-lg shadow-violet-500/5">
              <div className="text-center space-y-4">
                <div className="space-y-3">
                  <p className="text-sm text-violet-700 dark:text-violet-300 font-medium uppercase tracking-wider">
                    <span className="inline-flex items-center gap-1.5">
                      Relationship Tension Score (RTI)
                      <InfoHint title="Relationship Tension Score (RTI)" description="Your current tension snapshot from check-ins. Lower RTI usually means calmer conditions." />
                    </span>
                  </p>
                  <HomeRtiRing score={rti.score} state={rti.state} />
                  <div className="flex items-center justify-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStateColor(rti.state)}`} />
                    <p className="text-neutral-700 dark:text-neutral-300 font-medium">{rti.state}</p>
                  </div>
                </div>

                {/* Forecast */}
                {forecast && (() => {
                  const ForecastIcon = forecast.icon;
                  if (typeof ForecastIcon !== 'function') return null;
                  return (
                    <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
                      <div className="flex items-center justify-center gap-2">
                        <ForecastIcon className={`w-5 h-5 ${forecast.color}`} />
                        <p className={`text-sm ${forecast.color}`}>Next 3 days: {forecast.text}</p>
                      </div>
                      <p className="mt-2 text-center text-xs text-neutral-500 dark:text-neutral-400">
                        Chance tension rises: {forecast.probabilityRisePct}% · confidence {forecast.confidencePct}%
                      </p>
                      {forecast.churnRisk && (
                        <p className="mt-1 text-center text-xs text-neutral-500 dark:text-neutral-400">
                          Early warning (disconnect risk): {forecast.churnRisk.riskPct}% ({forecast.churnRisk.level})
                        </p>
                      )}
                      {Array.isArray(forecast.patterns) && forecast.patterns.length > 0 && (
                        <p className="mt-1 text-center text-xs text-neutral-500 dark:text-neutral-400">
                          Pattern we noticed: {forecast.patterns[0].label}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Insight */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <p className="text-sm text-neutral-700 text-center">
                  {rti.state === 'Stable' && 'Things look calm and balanced right now.'}
                  {rti.state === 'Elastic' && 'Some ups and downs are showing. Small adjustments can help.'}
                  {rti.state === 'High Tension' && 'Pressure is high right now. Slow, steady moves are best.'}
                </p>
                {forecast?.scripts?.[0]?.text && (
                  <p className="mt-3 text-xs text-neutral-500 text-center">
                    Suggested line: "{forecast.scripts[0].text}"
                  </p>
                )}
              </div>
            </Card>

            {/* Prominent Share Button - Invite Friends & Earn Rewards */}
            <NamedHomeErrorBoundary name="EnhancedShareButton">
              <EnhancedShareButton
                variant="prominent"
                currentRTI={rti.score}
                previousRTI={rti.previousScore}
                streak={safeLocalStorageGet('tension_streak') ? parseInt(safeLocalStorageGet('tension_streak') || '0', 10) : 0}
                partnerName={activeRelationship?.name}
                showRTIShare={true}
              />
            </NamedHomeErrorBoundary>

            {/* Phase 4C: Rumination Detection (Pro) - Shows alert if rumination detected */}
            <NamedHomeErrorBoundary name="RuminationDetection">
              {subscription === 'pro' && <RuminationDetection tier={subscription as any} />}
            </NamedHomeErrorBoundary>

            {/* Smart Contextual Prompts - Proactive insights */}
            <NamedHomeErrorBoundary name="ContextualPrompts">
              <ContextualPrompts />
            </NamedHomeErrorBoundary>

            {/* Action Card */}
            {action && (
              <Card className="p-6 bg-neutral-900 text-white">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-neutral-400 uppercase tracking-wider">Today&apos;s next step</p>
                      <p className="text-lg mt-2">{action.action}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border-white text-white hover:bg-white hover:text-neutral-900"
                    onClick={() => navigate('/action')}
                  >
                    See step-by-step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            )}

            {/* Micro-Actions: Active Coaching Throughout Day */}
            <NamedHomeErrorBoundary name="MicroActions">
              <MicroActions />
            </NamedHomeErrorBoundary>

            {/* Phase 4A Features - Restraint Timer */}
            <NamedHomeErrorBoundary name="RestraintTimer">
              {rti.state !== 'Stable' && <RestraintTimer />}
            </NamedHomeErrorBoundary>

            {/* Check-In Streaks */}
            <NamedHomeErrorBoundary name="CheckInStreaks">
              <CheckInStreaks />
            </NamedHomeErrorBoundary>

            {/* Monthly Retrospective */}
            <NamedHomeErrorBoundary name="MonthlyRetrospective">
              <MonthlyRetrospective />
            </NamedHomeErrorBoundary>

            {/* Phase 4B: Weekly Report Card (Premium+) */}
            {activeRelationshipId && (
              <NamedHomeErrorBoundary name="WeeklyReportCard">
                <WeeklyReportCard
                  relationshipId={activeRelationshipId}
                  tier={subscription as any}
                />
              </NamedHomeErrorBoundary>
            )}

            {/* Phase 4B: Communication Templates (Premium+) */}
            <NamedHomeErrorBoundary name="CommunicationTemplates">
              <CommunicationTemplates
                rtiScore={rti.score}
                tier={subscription as any}
              />
            </NamedHomeErrorBoundary>

            {/* 72-Hour Forecast - Premium Feature */}
            {isPremium ? (
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-blue-600" />
                    <p className="text-xs text-blue-900 uppercase tracking-wider font-medium inline-flex items-center gap-1.5">Next 3-Day Outlook <InfoHint title="Next 3-Day Outlook" description="A directional estimate of likely score movement over the next three days if current pattern continues." /></p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-700">Tomorrow</span>
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-neutral-900">
                          {Math.max(0, rti.score - 8)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-700">In 2 Days</span>
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-neutral-900">
                          {Math.max(0, rti.score - 15)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-700">In 3 Days</span>
                      <div className="flex items-center gap-2">
                        <Minus className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-neutral-900">
                          {Math.max(0, rti.score - 22)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs text-neutral-700">
                      {rti.state === 'High Tension' && 'This outlook suggests improvement if you keep pressure low for now.'}
                      {rti.state === 'Elastic' && 'Small communication tweaks should steady things over the next 3 days.'}
                      {rti.state === 'Stable' && 'This outlook suggests your current healthy pattern can continue.'}
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-6 bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-700 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-200/50 to-transparent dark:from-neutral-700/50" />
                <div className="relative space-y-4">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-neutral-600 dark:text-neutral-400" aria-hidden="true" />
                    <p className="text-xs text-neutral-600 dark:text-neutral-400 uppercase tracking-wider font-medium">Next 3-Day Outlook</p>
                  </div>
                  <div className="space-y-3 blur-sm select-none">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">Tomorrow</span>
                      <span className="text-sm font-medium text-neutral-700">--</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">In 2 Days</span>
                      <span className="text-sm font-medium text-neutral-700">--</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">In 3 Days</span>
                      <span className="text-sm font-medium text-neutral-700">--</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => navigate('/pricing')}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Premium
                  </Button>
                </div>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  trackCoreAction('open_daily_checkin_secondary');
                  navigate('/check-in');
                }}
                className="h-16 sm:h-20"
              >
                Start check-in
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  trackCoreAction('open_timeline_secondary');
                  navigate('/timeline');
                }}
                className="h-16 sm:h-20"
              >
                Open timeline
              </Button>
            </div>

            {/* Situational Packs CTA */}
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-medium text-neutral-900">Guided support packs</h3>
                </div>
                <p className="text-sm text-neutral-700">
                  Get focused support for breakup recovery, reconnecting, or conflict repair.
                </p>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <span>$2.99 each</span>
                  <span className="text-neutral-400">•</span>
                  <span>One-time purchase</span>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/packs')}
                >
                  Browse support packs
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </>
        )}
        </HomeFlowSection>
      </div>
    </div>
  );
}

export default Home;